import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Card, Button, Badge, Form, Row, Col, Modal, Tabs, Tab } from 'react-bootstrap';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { PurchaseRequest, Workflow, Group, WorkflowCustomField, User, DocumentInstance } from '../types';
import { API_URL } from '../config';
import './Tickets.css';
import './Dashboard.css';
import useAlert from '../hooks/useAlert';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import DataTable, { Column } from '../components/DataTable';
import FilterPanel, {
  ActiveFilter,
  FilterControl,
  FilterDropdownSelect,
  FilterField,
  FilterSection,
  FilterToggle,
} from '../components/filters/FilterPanel';
import StatusMultiFilter from '../components/filters/StatusMultiFilter';
import {
  parseStatusesFromSearchParams,
  restoreDashboardStatuses,
  serializeDashboardParams,
  statusesForArea,
} from '../utils/dashboardStatusFilter';
import DraftListModal from '../components/DraftListModal';
import CustomTableField from '../components/CustomTableField';
import SupplierFieldControl from '../components/SupplierFieldControl';
import { createDefaultTableValue, hasTableContent, serializeTableValue } from '../utils/tableCustomField';
import { shouldShowConditionalField } from '../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';
import { formatFastApiValidationErrors } from '../utils/apiError';
import { pickValueRangeValidationForNumericValue, coerceMinDateDaysValue } from '../utils/valueRangeValidation';
import { searchPurchaseFormUsers } from '../utils/searchUsers';
import {
  getSupplierSelectionCount,
  isSupplierField as isSupplierCustomField,
  normalizeSupplierFieldRawValue,
  validateSupplierSelectionValue,
} from '../utils/supplierSelection';
import { URGENCY_OPTIONS, getUrgencyColor, getUrgencyLabel, type TicketUrgency } from '../utils/urgency';
import { getPriorityColor, getPriorityLabel } from '../utils/priority';
import { canManagePurchasePriority } from '../utils/roles';
import { userMayBeRequesterAndApprover } from '../utils/userPermissions';
import {
  findWorkflowFieldById,
  parseValidationRulesJson,
  isValidationSourceValueEmpty,
  parseCustomFieldNumericValue,
} from '../utils/customFieldValidation';
import {
  addDaysLocal,
  formatDateInputLocal,
  parseDateInputLocal,
  shouldPrefillDateField,
  startOfTodayLocal,
} from '../utils/customFieldDate';
import { getAuthToken } from '../utils/authToken';
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
  validateAttachmentFiles,
} from '../utils/uploadValidation';
import {
  hasSavedCustomFieldFile,
  parseSavedCustomFieldFilesFromApi,
  savedCustomFieldFileLabel,
  uploadPurchaseCustomFieldFiles,
  type SavedCustomFieldFile,
} from '../utils/purchaseCustomFieldFiles';
import {
  hasCheckFieldPersistedValue,
  hydrateCustomFieldTextValue,
  serializeCheckFieldValue,
} from '../utils/purchaseCustomFieldValues';
import {
  upsertPurchaseCustomFieldValue,
  batchUpsertPurchaseCustomFieldValues,
  isBatchEndpointUnavailable,
} from '../utils/purchaseCustomFieldUpsert';
import { partitionFieldsForSave } from '../utils/customFieldSaveOrder';
import {
  buildPurchaseDraftPayload,
  purchaseDraftFinalizeUrl,
  purchaseDraftItemUrl,
  PURCHASE_DRAFT_STATUS,
  savePurchaseDraftRecord,
  throwNotADraftForSubmit,
} from '../utils/purchaseDraftApi';
import {
  CLOSED_VISIBILITY_OPTIONS,
  ClosedVisibility,
  DEFAULT_CLOSED_VISIBILITY,
  closedVisibilityChipLabel,
  closedVisibilityNotice,
  normalizeClosedVisibility,
} from '../utils/closedItemsVisibility';

const PURCHASE_OBJECT_HELP_TEXT = 'Descreva, de maneira sucinta, o serviço ou material a ser contratado.';
const PURCHASE_ITEMS_HELP_TEXT = `Descreva de maneira detalhada os itens que compõem o objeto.

Se MATERIAL, cada item deverá ser detalhado apresentando a descrição/identificação completa.

Se SERVIÇO, todo detalhamento deverá estar incluso no memorial descritivo, exceto se aditivo prazo.
Independentemente da natureza, faz-se necessário a inclusão das seguintes informações: número serial, modelo, norma balizadora do item, quantidade, prazo de garantia, local e prazo de entrega ou execução dos serviços, suporte, entre outros.

Para mais de um item requisitado, todos necessariamente deverão ser relacionados aos mesmos fornecedores indicados posteriormente.`;
const PURCHASE_WORKFLOW_HELP_TEXT = `Selecione conforme instruções:

ADITIVO para alterações contratuais.

CANCELAMENTO/ENCERRAMENTO DE CONTRATO Deverá ser utilizado quando for necessário encerrar um contrato com o fornecedor antes do fim da vigência contratual ou quando se deseja cancelar um contrato em andamento.

CONTRATAÇÃO DIRETA se efetuada em regime de exceção para contratações de característica restrita.

CONTRATAÇÃO EMERGENCIAL para contratações com escopo que envolvam mitigação de riscos com prejuízos à imagem da empresa.

INOVAÇÃO Deverá ser utilizado quando a demanda for de contratação de inovação.

RENOVAÇÃO (Contratação Direta) contratação com nova relação contratual a ser firmada com o mesmo contratado.

RENOVAÇÃO (Processo Competitivo) contratação do mesmo objeto, por meio de nova relação contratual a ser firmada com o novo fornecedor.

RFI (Requisição de Informação ao Fornecedor) Deverá ser utilizado em situações em que se tem pouco conhecimento sobre o produto ou serviço a ser adquirido e seus possíveis fornecedores, mas não resulta em uma compra.

RFQ (Requisição de Cotação) Deverá ser utilizado quando se deseja conhecer o preço praticado pelo mercado. Não gera obrigatoriedade de contratação.

SOLICITAÇÃO DE COTAÇÃO para regime de contratação através da solicitação de propostas no mercado.`;

const Purchases: React.FC = () => {
  const [purchaseRequests, setPurchaseRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDraftListModal, setShowDraftListModal] = useState(false);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
  const [filterCreatedTo, setFilterCreatedTo] = useState('');
  const [filterCustomFieldId, setFilterCustomFieldId] = useState('');
  const [filterCustomFieldValue, setFilterCustomFieldValue] = useState('');
  const [closedVisibility, setClosedVisibility] = useState<ClosedVisibility>(DEFAULT_CLOSED_VISIBILITY);
  const [hiddenConcludedTotal, setHiddenConcludedTotal] = useState(0);
  const [includeThirdPartyTickets, setIncludeThirdPartyTickets] = useState<boolean>(true);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [filterCreatedByMe, setFilterCreatedByMe] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPurchaseRequests, setTotalPurchaseRequests] = useState(0);
  const [summaryCountsByStatus, setSummaryCountsByStatus] = useState<Record<string, number>>({});
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert, AlertComponent } = useAlert();
  const { showConfirm } = useModal();
  const { user, refreshUser } = useAuth();
  const canSeePurchasePriority = useMemo(() => canManagePurchasePriority(user), [user]);

  const workflowFilterOptions = useMemo(
    () => workflows.map((workflow) => ({ value: String(workflow.id), label: workflow.name })),
    [workflows],
  );

  const priorityFilterOptions = useMemo(
    () => [
      { value: 'p1', label: 'P1' },
      { value: 'p2', label: 'P2' },
      { value: 'p3', label: 'P3' },
      { value: 'p4', label: 'P4' },
    ],
    [],
  );

  const closedVisibilityFilterOptions = useMemo(
    () => CLOSED_VISIBILITY_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  // Atalho de descoberta: subtarefas ficam ocultas por padrão, então quem recebe
  // uma subtarefa individual precisa de um caminho direto até ela.
  const isMySubtasksViewActive =
    showSubtasks && !!user && filterAssignedTo === String(user.id);

  const toggleMySubtasksView = () => {
    if (!user) return;
    if (isMySubtasksViewActive) {
      setShowSubtasks(false);
      setFilterAssignedTo('');
      return;
    }
    setShowSubtasks(true);
    setFilterAssignedTo(String(user.id));
  };

  useEffect(() => {
    setFiltersInitialized(false);
    const params = new URLSearchParams(location.search);
    setFilterStatuses(statusesForArea('purchases', parseStatusesFromSearchParams(params)));
    setFilterUrgency(params.get('urgency') || '');
    setFilterPriority(params.get('priority') || '');
    setFilterWorkflow(params.get('workflow_id') || '');
    setFilterGroup(params.get('assigned_group_id') || '');
    setFilterAssignedTo(params.get('assigned_to') || '');
    setFilterCreatedFrom(params.get('created_from') || '');
    setFilterCreatedTo(params.get('created_to') || '');
    setFilterCustomFieldId(params.get('custom_field_id') || '');
    setFilterCustomFieldValue(params.get('custom_field_value') || '');
    setClosedVisibility(normalizeClosedVisibility(params.get('closed_visibility')));
    setShowSubtasks(params.get('show_subtasks') === '1');
    setFilterCreatedByMe(params.get('created_by_me') === '1' || params.get('created_by_me') === 'true');
    setSearchText(params.get('search') || '');
    setCurrentPage(1);
    setFiltersInitialized(true);
  }, [location.search]);

  useEffect(() => {
    fetchWorkflows();
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!user) return;
    setIncludeThirdPartyTickets(user.include_third_party_tickets !== false);
  }, [user?.id, user?.include_third_party_tickets]);

  // Resetar para página 1 quando filtros mudarem
  useEffect(() => {
    if (!filtersInitialized) return;
    setCurrentPage(1);
  }, [filterStatuses, filterUrgency, filterPriority, filterWorkflow, filterGroup, filterAssignedTo, filterCreatedFrom, filterCreatedTo, filterCustomFieldId, filterCustomFieldValue, closedVisibility, includeThirdPartyTickets, showSubtasks, filterCreatedByMe, searchText, filtersInitialized]);

  useEffect(() => {
    if (!filtersInitialized) return;
    if (!canSeePurchasePriority) {
      if (filterPriority) setFilterPriority('');
      if (sortColumn === 'priority') {
        setSortColumn('created_at');
        setSortDirection('desc');
      }
    }
  }, [filtersInitialized, canSeePurchasePriority, filterPriority, sortColumn]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000 },
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      // Para criação de requisições de compras, buscar apenas workflows publicados do tipo purchases
      const response = await axios.get(`${API_URL}/api/workflows/published`, {
        params: { area_type: 'purchases' }
      });
      let loadedWorkflows = (response.data || []).filter(
        (workflow: Workflow) => String(workflow?.name || '').trim() !== '__OPPORTUNITIES_MODULE__'
      );

      // Fallback local: quando não houver workflow publicado, listar ativos não publicados.
      if (loadedWorkflows.length === 0) {
        const fallbackResponse = await axios.get(`${API_URL}/api/workflows`, {
          params: { area_type: 'purchases', only_valid: false, limit: 1000 }
        });
        loadedWorkflows = (fallbackResponse.data || []).filter(
          (workflow: Workflow) =>
            workflow?.is_active &&
            String(workflow?.name || '').trim() !== '__OPPORTUNITIES_MODULE__'
        );
      }

      setWorkflows(loadedWorkflows);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const fetchPurchaseRequests = useCallback(async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const params: any = {
        skip,
        limit: itemsPerPage,
        sort_by: sortColumn === 'creator' ? 'created_by' : sortColumn,
        sort_order: sortDirection,
      };
      if (!showSubtasks) {
        params.parent_request_id = 0; // Padrão: listar apenas requisições principais
      }
      const selectedStatuses = statusesForArea('purchases', filterStatuses);
      if (selectedStatuses.length > 0) params.statuses = selectedStatuses;
      if (filterUrgency) params.urgency = filterUrgency;
      if (canSeePurchasePriority && filterPriority) params.priority = filterPriority;
      if (filterWorkflow) params.workflow_id = filterWorkflow;
      if (filterGroup) params.assigned_group_id = filterGroup;
      if (filterAssignedTo) params.assigned_to = filterAssignedTo;
      if (filterCreatedFrom) params.created_from = filterCreatedFrom;
      if (filterCreatedTo) params.created_to = filterCreatedTo;
      if (filterCustomFieldId) params.custom_field_id = filterCustomFieldId;
      if (filterCustomFieldValue) params.custom_field_value = filterCustomFieldValue;
      if (searchText) params.search = searchText;
      if (filterCreatedByMe) params.created_by_me = true;
      if (closedVisibility !== DEFAULT_CLOSED_VISIBILITY) params.closed_visibility = closedVisibility;
      params.include_third_party_tickets = user ? includeThirdPartyTickets : true;

      const summaryParams = { ...params };
      delete summaryParams.skip;
      delete summaryParams.limit;
      delete summaryParams.sort_by;
      delete summaryParams.sort_order;

      const requestConfig = {
        params,
        paramsSerializer: { serialize: serializeDashboardParams },
      };
      const [purchaseRequestsResponse, summaryResponse] = await Promise.all([
        axios.get(`${API_URL}/api/purchases`, requestConfig),
        axios.get(`${API_URL}/api/purchases/dashboard-summary`, {
          params: summaryParams,
          paramsSerializer: { serialize: serializeDashboardParams },
        }),
      ]);

      setPurchaseRequests(purchaseRequestsResponse.data);
      setTotalPurchaseRequests(summaryResponse.data?.total || 0);
      setSummaryCountsByStatus(summaryResponse.data?.counts_by_status || {});
      setHiddenConcludedTotal(summaryResponse.data?.hidden_concluded_total || 0);
    } catch (error) {
      console.error('Erro ao carregar requisições de compras:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatuses, filterUrgency, filterPriority, filterWorkflow, filterGroup, filterAssignedTo, filterCreatedFrom, filterCreatedTo, filterCustomFieldId, filterCustomFieldValue, closedVisibility, includeThirdPartyTickets, showSubtasks, filterCreatedByMe, searchText, currentPage, itemsPerPage, sortColumn, sortDirection, canSeePurchasePriority, user]);

  useEffect(() => {
    if (!filtersInitialized) return;
    fetchPurchaseRequests();
  }, [fetchPurchaseRequests, filtersInitialized]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'primary',
      aberto: 'warning',
      em_andamento: 'info',
      aguardando_informacao_usuario: 'secondary',
      aguardando_terceiros: 'secondary',
      aguardando_evidencia: 'secondary',
      aguardando_problema: 'secondary',
      aguardando_implantacao: 'secondary',
      aguardando_aprovacao: 'primary',
      aguardando_reenvio: 'danger',
      resolvido: 'success',
      cancelado: 'danger',
      fechado: 'dark',
    };
    return colors[status] || 'secondary';
  };

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      novo: 'Novo',
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      aguardando_informacao_usuario: 'Aguardando Informação do Usuário',
      aguardando_terceiros: 'Aguardando Terceiros',
      aguardando_evidencia: 'Aguardando Evidência',
      aguardando_problema: 'Aguardando Problema',
      aguardando_implantacao: 'Aguardando Implantação',
      aguardando_aprovacao: 'Aguardando Aprovação',
      aguardando_reenvio: 'Aguardando Reenvio',
      resolvido: 'Resolvido',
      cancelado: 'Cancelado',
      fechado: 'Fechado',
      rascunho: 'Rascunho',
    };
    return labels[status] || status;
  };

  const handleEditDraft = (draftId: number) => {
    // Usar localStorage para passar o draftId selecionado (mesma lógica do DraftListModal)
    localStorage.setItem('selected_draft_id', String(draftId));
    setShowModal(true);
  };

  const handleDeleteDraft = async (draftId: number) => {
    const ok = await showConfirm({
      title: 'Excluir rascunho',
      message: 'Tem certeza que deseja excluir este rascunho?',
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    
    try {
      // Deletar via endpoint de update mudando status para cancelado
      await axios.put(`${API_URL}/api/purchases/${draftId}`, {
        status: 'cancelado'
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      // Atualizar lista de requisições
      fetchPurchaseRequests();
      
      showAlert({
        title: 'Sucesso',
        message: 'Rascunho excluído com sucesso!',
        variant: 'success'
      });
    } catch (error: any) {
      console.error('Erro ao excluir rascunho:', error);
      let errorMessage = 'Erro ao excluir rascunho. Tente novamente.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      showAlert({
        title: 'Erro',
        message: errorMessage,
        variant: 'danger',
      });
    }
  };

  const handleThirdPartyFilterChange = async (checked: boolean) => {
    const previous = includeThirdPartyTickets;
    setIncludeThirdPartyTickets(checked);
    try {
      await axios.put(
        `${API_URL}/api/auth/me/include-third-party-tickets?include_third_party_tickets=${checked}`
      );
      await refreshUser();
    } catch (error) {
      setIncludeThirdPartyTickets(previous);
      console.error('Erro ao salvar preferência de terceiros:', error);
      showAlert({
        title: 'Aviso',
        message: 'Não foi possível salvar o padrão de visualização de requisições de terceiros.',
        variant: 'warning',
      });
    }
  };

  const getDisplayId = (purchaseRequest: PurchaseRequest): string =>
    purchaseRequest.public_id || String(purchaseRequest.id);

  const columns: Column<PurchaseRequest>[] = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (purchaseRequest) => `#${getDisplayId(purchaseRequest)}`,
    },
    {
      key: 'title',
      label: 'Título',
      sortable: true,
      render: (purchaseRequest) => {
        const parentId =
          purchaseRequest.parent_request_id ?? purchaseRequest.parent_purchase_request_id;
        return (
        <div>
          {parentId && (
            <Badge bg="info" className="me-2">
              {purchaseRequest.is_individual_subtask ? 'Subtarefa individual' : 'Subtarefa'}
            </Badge>
          )}
          <span>{purchaseRequest.title}</span>
          {parentId && (
            <div className="mt-1">
              <small className="text-muted">
                Requisição pai: <Link to={`/purchases/${parentId}`}>#{purchaseRequest.parent_public_id || parentId}</Link>
              </small>
            </div>
          )}
        </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (purchaseRequest) => (
        <Badge bg={getStatusColor(purchaseRequest.status)}>
          {getStatusLabel(purchaseRequest.status)}
        </Badge>
      ),
    },
    {
      key: 'urgency',
      label: 'Urgência',
      sortable: true,
      render: (purchaseRequest) => (
        <Badge bg={getUrgencyColor(purchaseRequest.urgency)}>
          {getUrgencyLabel(purchaseRequest.urgency)}
        </Badge>
      ),
    },
    {
      key: 'assignee',
      label: 'Analista',
      sortable: false,
      render: (purchaseRequest) => purchaseRequest.assignee?.name || 'Não atribuído',
    },
    ...(canSeePurchasePriority
      ? [
          {
            key: 'priority',
            label: 'Prioridade',
            sortable: true,
            render: (purchaseRequest: PurchaseRequest) =>
              purchaseRequest.priority ? (
                <Badge bg={getPriorityColor(purchaseRequest.priority)}>
                  {getPriorityLabel(purchaseRequest.priority)}
                </Badge>
              ) : (
                '—'
              ),
          } as Column<PurchaseRequest>,
        ]
      : []),
    {
      key: 'creator',
      label: 'Criado por',
      sortable: false,
      render: (purchaseRequest) => purchaseRequest.creator?.name || 'N/A',
    },
    {
      key: 'created_at',
      label: 'Data',
      sortable: true,
      render: (purchaseRequest) => new Date(purchaseRequest.created_at).toLocaleDateString('pt-BR'),
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (purchaseRequest) => (
        <div className="d-flex gap-1" style={{ minWidth: '150px', flexWrap: 'nowrap' }}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate(`/purchases/${purchaseRequest.id}`)}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Ver
          </Button>
          {purchaseRequest.status === 'rascunho' && (
            <>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => handleEditDraft(purchaseRequest.id)}
                title="Editar rascunho"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                <i className="bi bi-pencil me-1"></i>
                Editar
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteDraft(purchaseRequest.id)}
                title="Excluir rascunho"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                <i className="bi bi-trash me-1"></i>
                Excluir
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];
  // Preferência salva do usuário: só vira chip quando a visualização atual foge dela.
  const thirdPartyDefault = user ? user.include_third_party_tickets !== false : true;

  const handleClearFilters = () => {
    setFilterStatuses([]);
    setFilterUrgency('');
    setFilterPriority('');
    setFilterWorkflow('');
    setFilterGroup('');
    setFilterAssignedTo('');
    setFilterCreatedFrom('');
    setFilterCreatedTo('');
    setFilterCustomFieldId('');
    setFilterCustomFieldValue('');
    setClosedVisibility(DEFAULT_CLOSED_VISIBILITY);
    setShowSubtasks(false);
    setFilterCreatedByMe(false);
    setSearchText('');
    setCurrentPage(1);
    if (user) {
      setIncludeThirdPartyTickets(thirdPartyDefault);
    }
    navigate('/purchases', { replace: true });
  };

  const activeFilters: ActiveFilter[] = [];
  {
    const selectedWorkflow = workflows.find((workflow) => String(workflow.id) === filterWorkflow);
    const selectedGroup = groups.find((group) => String(group.id) === filterGroup);

    if (searchText.trim()) {
      activeFilters.push({
        id: 'search',
        label: `Busca: "${searchText.trim()}"`,
        onRemove: () => setSearchText(''),
      });
    }
    if (filterWorkflow) {
      activeFilters.push({
        id: 'workflow',
        label: `Fluxo: ${selectedWorkflow?.name || `#${filterWorkflow}`}`,
        onRemove: () => setFilterWorkflow(''),
      });
    }
    statusesForArea('purchases', filterStatuses).forEach((statusValue) => {
      activeFilters.push({
        id: `status-${statusValue}`,
        label: `Status: ${getStatusLabel(statusValue)}`,
        onRemove: () => setFilterStatuses((current) => current.filter((item) => item !== statusValue)),
      });
    });
    if (filterUrgency) {
      activeFilters.push({
        id: 'urgency',
        label: `Urgência: ${getUrgencyLabel(filterUrgency)}`,
        onRemove: () => setFilterUrgency(''),
      });
    }
    if (filterPriority && canSeePurchasePriority) {
      activeFilters.push({
        id: 'priority',
        label: `Prioridade: ${getPriorityLabel(filterPriority)}`,
        onRemove: () => setFilterPriority(''),
      });
    }
    const closedVisibilityChip = closedVisibilityChipLabel(closedVisibility);
    if (closedVisibilityChip) {
      activeFilters.push({
        id: 'closed-visibility',
        label: closedVisibilityChip,
        onRemove: () => setClosedVisibility(DEFAULT_CLOSED_VISIBILITY),
      });
    }
    // Critérios que só chegam por drill-down: sem controle próprio, mas removíveis pelo chip.
    if (filterGroup) {
      activeFilters.push({
        id: 'group',
        label: `Grupo: ${selectedGroup?.name || `#${filterGroup}`}`,
        onRemove: () => setFilterGroup(''),
      });
    }
    if (filterAssignedTo) {
      activeFilters.push({
        id: 'assigned-to',
        label: `Responsável: ${filterAssignedTo}`,
        onRemove: () => setFilterAssignedTo(''),
      });
    }
    if (filterCreatedFrom) {
      activeFilters.push({
        id: 'created-from',
        label: `Criado de: ${filterCreatedFrom}`,
        onRemove: () => setFilterCreatedFrom(''),
      });
    }
    if (filterCreatedTo) {
      activeFilters.push({
        id: 'created-to',
        label: `Criado até: ${filterCreatedTo}`,
        onRemove: () => setFilterCreatedTo(''),
      });
    }
    if (filterCustomFieldId) {
      activeFilters.push({
        id: 'custom-field',
        label: `Campo customizado: #${filterCustomFieldId}`,
        onRemove: () => {
          setFilterCustomFieldId('');
          setFilterCustomFieldValue('');
        },
      });
    }
    if (filterCustomFieldValue) {
      activeFilters.push({
        id: 'custom-field-value',
        label: `Valor customizado: ${filterCustomFieldValue}`,
        onRemove: () => setFilterCustomFieldValue(''),
      });
    }
    if (user && includeThirdPartyTickets !== thirdPartyDefault) {
      activeFilters.push({
        id: 'third-party',
        label: `Terceiros: ${includeThirdPartyTickets ? 'Incluídos' : 'Ocultos'}`,
        onRemove: () => handleThirdPartyFilterChange(thirdPartyDefault),
      });
    }
    if (showSubtasks) {
      activeFilters.push({
        id: 'subtasks',
        label: 'Subtarefas: Incluídas',
        onRemove: () => setShowSubtasks(false),
      });
    }
    if (filterCreatedByMe) {
      activeFilters.push({
        id: 'created-by-me',
        label: 'Apenas itens criados por mim',
        onRemove: () => setFilterCreatedByMe(false),
      });
    }
  }

  const closedVisibilityMessage = useMemo(
    () =>
      closedVisibilityNotice(closedVisibility, hiddenConcludedTotal, {
        singular: 'requisição',
        plural: 'requisições',
      }),
    [closedVisibility, hiddenConcludedTotal]
  );

  const purchaseStats = useMemo(() => {
    let pendingApproval = 0;
    let inProgress = 0;
    let completed = 0;

    for (const [status, count] of Object.entries(summaryCountsByStatus)) {
      const n = Number(count) || 0;
      if (status === 'aguardando_aprovacao') pendingApproval += n;
      if (status === 'em_andamento' || status.startsWith('aguardando_')) inProgress += n;
      if (status === 'resolvido' || status === 'fechado') completed += n;
    }

    return { pendingApproval, inProgress, completed };
  }, [summaryCountsByStatus]);

  return (
    <div className="dashboard-page purchases-page">
      <div className="dashboard-hero mb-3">
        <div className="d-flex justify-content-between align-items-center dashboard-header">
          <div>
            <h2 className="dashboard-title mb-1">Compras e Contratações</h2>
            <small className="text-muted">
              Central de requisições com filtros operacionais, rastreabilidade e gestão de pendências.
            </small>
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button variant="outline-info" onClick={() => setShowDraftListModal(true)}>
              <i className="bi bi-file-earmark-text me-2"></i>
              Ver Rascunhos
            </Button>
            <Button variant="success" onClick={() => setShowModal(true)}>
              Nova Requisição
            </Button>
          </div>
        </div>
      </div>

      <FilterPanel
        panelId="purchases-filters-panel"
        activeFilters={activeFilters}
        expanded={showFilters}
        onToggleExpanded={() => setShowFilters((prev) => !prev)}
        onClearAll={handleClearFilters}
        notice={closedVisibilityMessage}
      >
        <FilterSection title="Busca e escopo">
          <FilterField label="Texto" controlId="purchases-filter-search" wide>
            <FilterControl
              id="purchases-filter-search"
              type="text"
              placeholder="Título ou descrição; para ID use #número"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </FilterField>
          <FilterField label="Fluxo" controlId="purchases-filter-workflow">
            <FilterDropdownSelect
              id="purchases-filter-workflow"
              value={filterWorkflow}
              onChange={setFilterWorkflow}
              options={workflowFilterOptions}
              placeholderLabel="Todos"
            />
          </FilterField>
        </FilterSection>

        <FilterSection title="Refinar resultados">
          <FilterField
            label="Status"
            controlId="purchases-filter-status"
            hint="Segure Ctrl (Cmd no Mac) e clique para combinar status. Sem seleção: todos."
          >
            <StatusMultiFilter
              idPrefix="purchases-filter-status"
              area="purchases"
              selected={statusesForArea('purchases', filterStatuses)}
              onChange={setFilterStatuses}
            />
          </FilterField>
          <FilterField label="Urgência" controlId="purchases-filter-urgency">
            <FilterDropdownSelect
              id="purchases-filter-urgency"
              value={filterUrgency}
              onChange={setFilterUrgency}
              options={URGENCY_OPTIONS}
              placeholderLabel="Todas"
            />
          </FilterField>
          {canSeePurchasePriority && (
            <FilterField label="Prioridade" controlId="purchases-filter-priority">
              <FilterDropdownSelect
                id="purchases-filter-priority"
                value={filterPriority}
                onChange={setFilterPriority}
                options={priorityFilterOptions}
                placeholderLabel="Todas"
              />
            </FilterField>
          )}
          <FilterField
            label="Conclusão"
            controlId="purchases-filter-closed-visibility"
            hint="Vale para requisições fechadas e canceladas, pela data de conclusão."
          >
            <FilterDropdownSelect
              id="purchases-filter-closed-visibility"
              value={closedVisibility}
              onChange={(value) => setClosedVisibility(normalizeClosedVisibility(value))}
              options={closedVisibilityFilterOptions}
              showEmptyOption={false}
            />
          </FilterField>
        </FilterSection>

        <FilterSection title="Exibição" layout="inline">
          <FilterToggle
            id="purchases-filter-third-party"
            label={includeThirdPartyTickets ? 'Incluir requisições de terceiros' : 'Ocultar requisições de terceiros'}
            checked={includeThirdPartyTickets}
            onChange={handleThirdPartyFilterChange}
          />
          <FilterToggle
            id="show-subtasks-switch"
            label={showSubtasks ? 'Incluir subtarefas' : 'Ocultar subtarefas'}
            checked={showSubtasks}
            onChange={setShowSubtasks}
          />
          <FilterToggle
            id="purchases-filter-created-by-me"
            label={filterCreatedByMe ? 'Apenas itens criados por mim' : 'Mostrar itens de todos os autores'}
            checked={filterCreatedByMe}
            onChange={setFilterCreatedByMe}
          />
        </FilterSection>
      </FilterPanel>

      <Row className="g-3 mb-3">
        <Col sm={6} xl={3}>
          <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
            <Card.Body>
              <p className="dashboard-stat-title mb-1">Total filtrado</p>
              <h4 className="dashboard-stat-value text-primary">{totalPurchaseRequests}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} xl={3}>
          <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
            <Card.Body>
              <p className="dashboard-stat-title mb-1">Pendentes de aprovação</p>
              <h4 className="dashboard-stat-value text-warning">{purchaseStats.pendingApproval}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} xl={3}>
          <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
            <Card.Body>
              <p className="dashboard-stat-title mb-1">Em andamento</p>
              <h4 className="dashboard-stat-value text-info">{purchaseStats.inProgress}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} xl={3}>
          <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
            <Card.Body>
              <p className="dashboard-stat-title mb-1">Concluídas</p>
              <h4 className="dashboard-stat-value text-success">{purchaseStats.completed}</h4>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-section-card">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex flex-column">
            <span>Todas as Requisições ({totalPurchaseRequests})</span>
            <small className="text-muted">Ordenação e paginação abaixo da tabela.</small>
          </div>
        </Card.Header>
        <Card.Body>
          <DataTable
            className="tickets-datatable"
            data={purchaseRequests}
            columns={columns}
            loading={loading}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalPurchaseRequests}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            emptyMessage="Nenhuma requisição encontrada"
            tableClassName="dashboard-table table-sm align-middle mb-0"
          />
        </Card.Body>
      </Card>

      <PurchaseModal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        onSuccess={fetchPurchaseRequests} 
        workflows={workflows} 
        groups={groups}
        showAlert={showAlert}
      />
      <DraftListModal
        show={showDraftListModal}
        onHide={() => setShowDraftListModal(false)}
        onSelectDraft={async (draft) => {
          // Fechar modal de rascunhos
          setShowDraftListModal(false);
          // Abrir modal de criação - o modal já tem lógica para carregar rascunhos automaticamente
          // Vamos usar localStorage para passar o draftId selecionado
          if (draft.id) {
            localStorage.setItem('selected_draft_id', String(draft.id));
          }
          setShowModal(true);
        }}
        onDeleteDraft={(draftId) => {
          // Atualizar lista se necessário
        }}
      />
      <AlertComponent />
    </div>
  );
};

interface PurchaseModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  workflows: Workflow[];
  groups: Group[];
  showAlert: (options: { title: string; message: string; variant?: 'success' | 'danger' | 'warning' | 'info'; details?: string }) => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  show,
  onHide,
  onSuccess,
  workflows,
  groups,
  showAlert,
}) => {
  const { user: currentUser } = useAuth();
  const canManagePriority = canManagePurchasePriority(currentUser);
  const [object, setObject] = useState('');
  const [itemsDescription, setItemsDescription] = useState('');
  const [urgency, setUrgency] = useState<TicketUrgency | ''>('');
  const [priority, setPriority] = useState('p4');
  const [workflowId, setWorkflowId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<{ [fieldId: number]: any }>({});
  const [customFieldInlineErrors, setCustomFieldInlineErrors] = useState<{ [fieldId: number]: string }>({});
  const [customFieldFiles, setCustomFieldFiles] = useState<{ [fieldId: number]: File | File[] | null }>({});
  const [customFieldSavedFiles, setCustomFieldSavedFiles] = useState<{ [fieldId: number]: SavedCustomFieldFile }>({});
  const [users, setUsers] = useState<User[]>([]);
  const [analystUsers, setAnalystUsers] = useState<User[]>([]);
  const [searchUsers, setSearchUsers] = useState<{ [fieldId: number]: string }>({});
  const [filteredUsers, setFilteredUsers] = useState<{ [fieldId: number]: User[] }>({});
  const [purchaseUserPickerResults, setPurchaseUserPickerResults] = useState<{ [fieldId: number]: User[] }>({});
  const [pickedPurchaseCustomUsers, setPickedPurchaseCustomUsers] = useState<{ [fieldId: number]: User }>({});
  const [customFieldSuggestAnchor, setCustomFieldSuggestAnchor] = useState<{
    [fieldId: number]: { top: number; left: number; width: number };
  }>({});
  const purchaseUserPickerTimersRef = useRef<{ [fieldId: number]: ReturnType<typeof setTimeout> }>({});
  const [requesterId, setRequesterId] = useState<number | null>(null);
  const [requesterSearchTerm, setRequesterSearchTerm] = useState<string>('');
  const [debouncedRequesterQuery, setDebouncedRequesterQuery] = useState<string>('');
  const [filteredRequesterUsers, setFilteredRequesterUsers] = useState<User[]>([]);
  const [requesterSearchLoading, setRequesterSearchLoading] = useState(false);
  const [selectedRequester, setSelectedRequester] = useState<User | null>(null);
  const [documentInstances, setDocumentInstances] = useState<DocumentInstance[]>([]);
  const [documentInstancesByField, setDocumentInstancesByField] = useState<{ [fieldId: number]: DocumentInstance[] }>({});
  const [platformDocumentMode, setPlatformDocumentMode] = useState<{ [fieldId: number]: 'upload' | 'platform' | 'create' }>({});
  const [platformDocumentSelectedIds, setPlatformDocumentSelectedIds] = useState<{ [fieldId: number]: number | null }>({});
  const [draftId, setDraftId] = useState<number | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [tabKeys, setTabKeys] = useState<string[]>([]);
  const customFieldsRef = useRef<HTMLDivElement>(null);
  const groupsRequestSeqRef = useRef(0);
  const customFieldsRequestSeqRef = useRef(0);
  /** Evita que o efeito de workflowId apague valores/grupo ao hidratar rascunho. */
  const preserveDraftHydrationRef = useRef(false);
  /** Grupo do rascunho a reaplicar após first-contact-groups carregar (evita race). */
  const pendingDraftGroupIdRef = useRef<string | null>(null);

  // Carregar rascunho ao abrir modal
  // Solicitante fixo no usuário logado (rascunho e requisição)
  React.useEffect(() => {
    if (!show || !currentUser) return;
    setRequesterId(currentUser.id);
    setSelectedRequester(currentUser);
    setRequesterSearchTerm('');
    setDebouncedRequesterQuery('');
    setFilteredRequesterUsers([]);
  }, [show, currentUser]);

  React.useEffect(() => {
    if (show && currentUser) {
      const loadDraft = async () => {
        try {
          // Verificar se há um rascunho selecionado da lista
          const selectedDraftId = localStorage.getItem('selected_draft_id');
          if (selectedDraftId) {
            // Carregar rascunho selecionado
            try {
              // Isolar anexos: zerar antes de hidratar outro draft (evita mistura entre rascunhos)
              setCustomFieldSavedFiles({});
              setCustomFieldFiles({});

              const draftResponse = await axios.get(`${API_URL}/api/purchases/${selectedDraftId}`);
              const draft = draftResponse.data;
              
              setDraftId(draft.id);
              setIsDraft(true);
              // Usar object, ou title se object não existir (compatibilidade com rascunhos antigos)
              setObject(draft.object || draft.title || '');
              setItemsDescription(draft.items_description || '');
              setUrgency((draft.urgency as TicketUrgency) || '');
              setPriority(draft.priority || 'p4');
              const workflowIdStr = draft.workflow_id ? String(draft.workflow_id) : '';
              const groupIdStr = draft.assigned_group_id ? String(draft.assigned_group_id) : '';
              // Preservar valores ao setar workflowId (efeito de reload não deve zerar aceite/grupo)
              preserveDraftHydrationRef.current = true;
              pendingDraftGroupIdRef.current = groupIdStr || null;
              setWorkflowId(workflowIdStr);
              setGroupId(groupIdStr);
              // Solicitante fixo: sempre o usuário logado
              setRequesterId(currentUser.id);
              setSelectedRequester(currentUser);
              setRequesterSearchTerm('');
              setDebouncedRequesterQuery('');
              setFilteredRequesterUsers([]);
              
              // Carregar campos personalizados do workflow (sem exigir grupo)
              if (workflowIdStr) {
                try {
                  const fieldsResponse = await axios.get(`${API_URL}/api/workflows/${workflowIdStr}/custom-fields/first-step`);
                  setCustomFields(fieldsResponse.data || []);
                } catch (error) {
                  console.error('Erro ao carregar campos personalizados do workflow:', error);
                }
              }
              
              // Carregar valores dos campos personalizados salvos no rascunho
              const customFieldsResponse = await axios.get(`${API_URL}/api/purchases/${draft.id}/custom-fields`);
              if (customFieldsResponse.data) {
                const customValues: { [fieldId: number]: any } = {};
                const platformModes: { [fieldId: number]: 'upload' | 'platform' | 'create' } = {};
                const platformDocIds: { [fieldId: number]: number | null } = {};
                
                const savedFiles: { [fieldId: number]: SavedCustomFieldFile } = {};
                customFieldsResponse.data.forEach((cf: any) => {
                  // Verificar se é campo platform_document com document_instance_id
                  if (cf.document_instance_id) {
                    platformModes[cf.custom_field_id] = 'platform';
                    platformDocIds[cf.custom_field_id] = cf.document_instance_id;
                    customValues[cf.custom_field_id] = cf.document_instance_id.toString();
                  } else if (cf.file_path || cf.file_paths) {
                    const parsedSaved = parseSavedCustomFieldFilesFromApi(cf);
                    if (parsedSaved) {
                      savedFiles[cf.custom_field_id] = parsedSaved;
                    }
                  } else if (cf.text_value != null && String(cf.text_value) !== '') {
                    customValues[cf.custom_field_id] = hydrateCustomFieldTextValue(String(cf.text_value));
                  }
                });
                setCustomFieldValues(customValues);
                setCustomFieldSavedFiles(savedFiles);
                setCustomFieldFiles({});
                setPlatformDocumentMode(prev => ({ ...prev, ...platformModes }));
                setPlatformDocumentSelectedIds(prev => ({ ...prev, ...platformDocIds }));
              }

              // preserveDraftHydrationRef / pendingDraftGroupIdRef são liberados
              // pelos efeitos de groups e custom-fields após concluírem
              
              // Limpar o draftId selecionado
              localStorage.removeItem('selected_draft_id');
              return;
            } catch (error) {
              console.error('Erro ao carregar rascunho selecionado:', error);
              localStorage.removeItem('selected_draft_id');
            }
          }
        } catch (error) {
          console.error('Erro ao carregar rascunho:', error);
        }
      };
      
      loadDraft();
    } else if (!show) {
      // Limpar estados quando modal fecha
      setDraftId(null);
      setIsDraft(false);
      setRequesterId(null);
      setSelectedRequester(null);
      setRequesterSearchTerm('');
      setDebouncedRequesterQuery('');
      setFilteredRequesterUsers([]);
      setCustomFieldSavedFiles({});
      setCustomFieldFiles({});
      preserveDraftHydrationRef.current = false;
      pendingDraftGroupIdRef.current = null;
    }
  }, [show, currentUser]);

  const schedulePurchaseUserPickerQuery = useCallback((fieldId: number, rawTerm: string) => {
    const prevT = purchaseUserPickerTimersRef.current[fieldId];
    if (prevT) clearTimeout(prevT);
    const term = rawTerm.trim();
    if (!term) {
      setPurchaseUserPickerResults((p) => ({ ...p, [fieldId]: [] }));
      setCustomFieldSuggestAnchor((p) => {
        const n = { ...p };
        delete n[fieldId];
        return n;
      });
      return;
    }
    purchaseUserPickerTimersRef.current[fieldId] = setTimeout(async () => {
      try {
        const response = await axios.get<User[]>(`${API_URL}/api/users/purchase-picker`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          params: { search: term, limit: 40 },
        });
        setPurchaseUserPickerResults((p) => ({ ...p, [fieldId]: response.data }));
        const el = document.querySelector(
          `[data-custom-suggest-input="${fieldId}"]`
        ) as HTMLElement | null;
        if (el) {
          const r = el.getBoundingClientRect();
          setCustomFieldSuggestAnchor((p) => ({
            ...p,
            [fieldId]: { top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) },
          }));
        }
      } catch (err) {
        console.error(err);
        setPurchaseUserPickerResults((p) => ({ ...p, [fieldId]: [] }));
      }
    }, 280);
  }, []);

  React.useEffect(() => {
    if (show) return;
    setPurchaseUserPickerResults({});
    setPickedPurchaseCustomUsers({});
    setCustomFieldSuggestAnchor({});
    Object.values(purchaseUserPickerTimersRef.current).forEach((t) => clearTimeout(t));
    purchaseUserPickerTimersRef.current = {};
  }, [show]);

  React.useEffect(() => {
    if (!show) return;
    const ac = new AbortController();
    (async () => {
      for (const field of customFields) {
        if (field.field_type !== 'user') continue;
        const sid = Number(customFieldValues[field.id]);
        if (!Number.isFinite(sid) || sid < 1) continue;
        try {
          const r = await axios.get<User>(`${API_URL}/api/users/${sid}`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
            signal: ac.signal,
          });
          setPickedPurchaseCustomUsers((prev) =>
            prev[field.id]?.id === r.data.id ? prev : { ...prev, [field.id]: r.data }
          );
        } catch {
          /* ignore */
        }
      }
    })();
    return () => ac.abort();
  }, [show, customFields, customFieldValues]);

  // Carregar usuários para seleção de aprovadores
  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Usar endpoint específico para aprovadores (acessível a todos os usuários autenticados)
        const response = await axios.get(`${API_URL}/api/users/approvers`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          params: { limit: 1000 }
        });
        setUsers(response.data);
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
      }
    };
    if (show) {
      fetchUsers();
    }
  }, [show]);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedRequesterQuery(requesterSearchTerm);
    }, 350);
    return () => window.clearTimeout(t);
  }, [requesterSearchTerm]);

  React.useEffect(() => {
    const q = debouncedRequesterQuery.trim();
    if (q.length < 1) {
      setFilteredRequesterUsers([]);
      setRequesterSearchLoading(false);
      return;
    }
    const ac = new AbortController();
    setRequesterSearchLoading(true);
    (async () => {
      try {
        const items = await searchPurchaseFormUsers(q, getAuthToken(), {
          limit: 40,
          signal: ac.signal,
        });
        setFilteredRequesterUsers(items);
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.code === 'ERR_CANCELED') return;
        setFilteredRequesterUsers([]);
      } finally {
        setRequesterSearchLoading(false);
      }
    })();
    return () => ac.abort();
  }, [debouncedRequesterQuery]);

  React.useEffect(() => {
    if (!requesterId) {
      setSelectedRequester(null);
      return;
    }
    if (selectedRequester?.id === requesterId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const r = await axios.get<User>(`${API_URL}/api/users/${requesterId}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          signal: ac.signal,
        });
        setSelectedRequester(r.data);
      } catch {
        setRequesterId(null);
        setSelectedRequester(null);
      }
    })();
    return () => ac.abort();
  }, [requesterId, selectedRequester?.id]);

  // Carregar grupos do Primeiro Contato baseado no workflow selecionado
  React.useEffect(() => {
    const loadGroupsForWorkflow = async () => {
      const currentRequestSeq = ++groupsRequestSeqRef.current;
      const selectedWorkflowId = workflowId;
      if (selectedWorkflowId) {
        try {
          // Buscar grupos do Primeiro Contato do workflow
          const response = await axios.get(`${API_URL}/api/workflows/${selectedWorkflowId}/first-contact-groups`);
          if (currentRequestSeq !== groupsRequestSeqRef.current) return;
          // Filtrar grupos ativos e garantir que tenham nome válido
          const validGroups = (response.data || []).filter((g: Group) => {
            const isActive = g.is_active !== false; // Considerar ativo se não for explicitamente false
            const hasName = g.name && g.name.trim() !== '';
            return isActive && hasName;
          });
          setAvailableGroups(validGroups);
          const pendingGroupId = pendingDraftGroupIdRef.current;
          if (pendingGroupId !== null) {
            // Reaplicar grupo do rascunho somente depois que as opções existem no select
            const match = validGroups.some((g: Group) => String(g.id) === pendingGroupId);
            setGroupId(match ? pendingGroupId : '');
            pendingDraftGroupIdRef.current = null;
          } else if (!preserveDraftHydrationRef.current) {
            setGroupId(''); // Limpar grupo selecionado quando o usuário troca o workflow
          }
          
          if (validGroups.length === 0 && (response.data || []).length > 0) {
            console.warn('Nenhum grupo válido encontrado para o workflow. Grupos recebidos:', response.data);
          }
        } catch (error: any) {
          if (currentRequestSeq !== groupsRequestSeqRef.current) return;
          console.error('Erro ao carregar grupos do Primeiro Contato:', error);
          console.error('Detalhes do erro:', error.response?.data);
          setAvailableGroups([]);
          if (pendingDraftGroupIdRef.current !== null) {
            // Mantém o grupo do rascunho mesmo se a lista falhar; limpa o pending
            setGroupId(pendingDraftGroupIdRef.current);
            pendingDraftGroupIdRef.current = null;
          }
        }
      } else {
        setAvailableGroups([]);
        setGroupId('');
        pendingDraftGroupIdRef.current = null;
      }
    };

    loadGroupsForWorkflow();
  }, [workflowId]);

  // Carregar campos customizados baseado no workflow selecionado (sem exigir grupo)
  React.useEffect(() => {
    const loadCustomFieldsForWorkflow = async () => {
      const currentRequestSeq = ++customFieldsRequestSeqRef.current;
      const selectedWorkflowId = workflowId;
      const preserveHydration = preserveDraftHydrationRef.current;
      if (selectedWorkflowId) {
        if (!preserveHydration) {
          setCustomFields([]);
          setCustomFieldValues({});
        }
        try {
          // Buscar campos customizados da primeira etapa sem filtrar por grupo
          const fieldsResponse = await axios.get(`${API_URL}/api/workflows/${selectedWorkflowId}/custom-fields/first-step`);
          if (currentRequestSeq !== customFieldsRequestSeqRef.current) return;
          setCustomFields(fieldsResponse.data || []);
          
          // Inicializar apenas campos ainda sem valor após a troca de workflow
          setCustomFieldValues(prevValues => {
            const newValues: { [fieldId: number]: any } = { ...prevValues };
            (fieldsResponse.data || []).forEach((field: WorkflowCustomField) => {
              // Só inicializar se o campo não tiver valor ainda
              if (!(field.id in newValues)) {
                if (field.field_type === 'list' || field.field_type === 'combo') {
                  newValues[field.id] = field.allow_multiple ? [] : '';
                } else if (field.field_type === 'table') {
                  newValues[field.id] = createDefaultTableValue();
                } else if (field.field_type === 'currency') {
                  newValues[field.id] = JSON.stringify({ currency: 'BRL', value: '' });
                } else if (field.field_type === 'period') {
                  newValues[field.id] = JSON.stringify({ start: '', end: '' });
                } else {
                  newValues[field.id] = '';
                }
              }
            });
            return newValues;
          });
        } catch (error: any) {
          if (currentRequestSeq !== customFieldsRequestSeqRef.current) return;
          console.error('Erro ao carregar campos customizados:', error);
          console.error('Detalhes do erro:', error.response?.data);
          if (!preserveHydration) {
            setCustomFields([]);
            setCustomFieldValues({});
          }
        } finally {
          if (preserveHydration && currentRequestSeq === customFieldsRequestSeqRef.current) {
            preserveDraftHydrationRef.current = false;
          }
        }
      } else {
        setCustomFields([]);
        setCustomFieldValues({});
        preserveDraftHydrationRef.current = false;
      }
    };

    loadCustomFieldsForWorkflow();
  }, [workflowId]);

  // Processar valores de campos customizados quando campos são carregados
  // Isso garante que valores numéricos sejam convertidos corretamente para comparações condicionais
  React.useEffect(() => {
    if (customFields.length > 0 && Object.keys(customFieldValues).length > 0) {
      const processedValues: { [fieldId: number]: any } = { ...customFieldValues };
      let hasChanges = false;
      
      customFields.forEach(field => {
        const currentValue = processedValues[field.id];
        if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
          // Se o campo é numérico e o valor é uma string, converter para número
          if (['integer', 'decimal', 'currency'].includes(field.field_type)) {
            if (typeof currentValue === 'string') {
              // Tentar parsear como JSON primeiro (para currency que pode ser objeto)
              try {
                const parsed = JSON.parse(currentValue);
                if (typeof parsed === 'object' && parsed !== null) {
                  // É um objeto (currency ou period), manter como está
                  processedValues[field.id] = parsed;
                } else {
                  // É um número em string JSON, converter
                  processedValues[field.id] = typeof parsed === 'number' ? parsed : parseFloat(currentValue);
                  hasChanges = true;
                }
              } catch {
                // Não é JSON, tentar converter diretamente
                const numValue = parseFloat(currentValue);
                if (!isNaN(numValue)) {
                  processedValues[field.id] = numValue;
                  hasChanges = true;
                }
              }
            }
          }
        }
      });
      
      // Atualizar valores apenas se houver mudanças para evitar loops infinitos
      if (hasChanges) {
        setCustomFieldValues(processedValues);
      }
    }
  }, [customFields]); // Executar quando campos customizados mudarem

  // Calcular e atualizar abas quando campos personalizados mudarem
  React.useEffect(() => {
    if (customFields.length === 0) {
      setTabKeys([]);
      setActiveTab('');
      return;
    }

    const shouldShowField = (field: WorkflowCustomField): boolean =>
      shouldShowConditionalField(field, customFields, customFieldValues);

    // Agrupar campos por group_name
    const groupedFields: { [key: string]: WorkflowCustomField[] } = {};
    const ungroupedFields: WorkflowCustomField[] = [];

    customFields.forEach(field => {
      const isVisible = field.step_is_visible !== false;
      if (!isVisible) return;
      if (!shouldShowField(field)) return;
      
      if (field.group_name) {
        const groupName = field.group_name;
        if (!groupedFields[groupName]) {
          groupedFields[groupName] = [];
        }
        groupedFields[groupName].push(field);
      } else {
        ungroupedFields.push(field);
      }
    });

    const hasGroups = Object.keys(groupedFields).length > 0;
    const hasUngrouped = ungroupedFields.length > 0;
    
    const allTabKeys: string[] = [];
    Object.keys(groupedFields).forEach(key => allTabKeys.push(key));
    if (hasUngrouped) allTabKeys.push('ungrouped');
    
    setTabKeys(allTabKeys);
    if (allTabKeys.length > 0 && (!activeTab || !allTabKeys.includes(activeTab))) {
      setActiveTab(allTabKeys[0]);
    }
  }, [customFields, customFieldValues]);

  // Carregar usuários e analistas quando necessário
  React.useEffect(() => {
    const hasUserField = customFields.some(f => f.field_type === 'user');
    const hasAnalystField = customFields.some(f => f.field_type === 'analyst');
    const hasPlatformDocumentField = customFields.some(f => f.field_type === 'platform_document');

    const fetchData = async () => {
      if (hasUserField) {
        try {
          const response = await axios.get(`${API_URL}/api/users`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
          setUsers(response.data.filter((u: User) => u.is_active));
        } catch (error) {
          console.error('Erro ao buscar usuários:', error);
        }
      }

      if (hasAnalystField && groupId) {
        try {
          const response = await axios.get(`${API_URL}/api/groups/${groupId}`);
          const groupMembers = response.data.members || [];
          setAnalystUsers(groupMembers.map((m: any) => ({
            id: m.user_id,
            name: m.user_name,
            email: m.user_email,
            is_active: true
          })));
        } catch (error) {
          console.error('Erro ao buscar analistas do grupo:', error);
        }
      }

      if (hasPlatformDocumentField) {
        // Buscar documentos para cada campo platform_document com seu tipo específico
        const documentPromises = customFields
          .filter(f => f.field_type === 'platform_document')
          .map(async (field) => {
            try {
              const params: any = { status: 'finalized' };
              if (field.document_type_id) {
                params.document_type_id = field.document_type_id;
              }
              const response = await axios.get(`${API_URL}/api/documents/instances/available`, {
                params,
                headers: { Authorization: `Bearer ${getAuthToken()}` }
              });
              return { fieldId: field.id, documents: response.data };
            } catch (error) {
              console.error(`Erro ao buscar documentos para campo ${field.id}:`, error);
              return { fieldId: field.id, documents: [] };
            }
          });
        
        const results = await Promise.all(documentPromises);
        // Criar um mapa de fieldId -> documentos
        const documentsByField: { [fieldId: number]: DocumentInstance[] } = {};
        results.forEach(result => {
          documentsByField[result.fieldId] = result.documents;
        });
        setDocumentInstancesByField(documentsByField);
        // Para compatibilidade, manter documentInstances com todos os documentos (sem filtro)
        const allDocuments = results.flatMap(r => r.documents);
        const uniqueDocuments = Array.from(new Map(allDocuments.map(d => [d.id, d])).values());
        setDocumentInstances(uniqueDocuments);
      }
    };

    if (customFields.length > 0) {
      fetchData();
    }
  }, [customFields, groupId]);

  /** Campos de data cuja sugestão deve ser reposicionada por causa de uma edição do usuário. */
  const pendingDatePrefillRef = useRef<Set<number>>(new Set());

  const handleCustomFieldChange = (fieldId: number, value: any) => {
    // Editar qualquer campo pode mudar o prazo mínimo de uma data (Orçamento Estimado ou as
    // condições da faixa). Marcamos as datas para reposicionar a sugestão; o efeito de prefill
    // só age de fato se o mínimo calculado mudar.
    customFields.forEach((field) => {
      if (field.field_type === 'date' && field.validation_field_id && field.id !== fieldId) {
        pendingDatePrefillRef.current.add(field.id);
      }
    });
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Função para formatar valor monetário brasileiro
  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      'BRL': 'R$',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currency] || currency;
  };

  const formatCurrency = (value: string | number, currency: string = 'BRL'): string => {
    if (!value) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
    return `${getCurrencySymbol(currency)} ${formatted}`;
  };

  const parseFieldNumericValue = (rawValue: any, fieldType?: string): number =>
    parseCustomFieldNumericValue(rawValue, fieldType);

  const normalizeMultiCheckboxValues = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim());
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v).trim());
        }
        return [String(parsed).trim()];
      } catch {
        return value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
      }
    }
    if (value !== undefined && value !== null && value !== '') {
      return [String(value).trim()];
    }
    return [];
  };

  const evaluateConditionSatisfied = (condition: any, values: { [fieldId: number]: any }): boolean => {
    const conditionField = findWorkflowFieldById(customFields, condition.field_id);
    if (!conditionField) return false;

    const conditionValue = values[Number(condition.field_id)];
    const isMultiCheckbox = conditionField.field_type === 'check' && conditionField.allow_multiple;

    if (isMultiCheckbox) {
      const selectedOptions = normalizeMultiCheckboxValues(conditionValue);
      switch (condition.operator) {
        case 'checked':
          return selectedOptions.length > 0;
        case 'unchecked':
          return selectedOptions.length === 0;
        case 'equals': {
          const target = String(condition.value || '').trim();
          return selectedOptions.includes(target);
        }
        case 'not_equals': {
          const target = String(condition.value || '').trim();
          return !selectedOptions.includes(target);
        }
        default:
          return false;
      }
    }

    // Uma única escolha por texto (radio Sim/Não, lista ou combo): não é boolean
    const isSingleChoiceTextField =
      (conditionField.field_type === 'check' && !conditionField.allow_multiple) ||
      conditionField.field_type === 'list' ||
      conditionField.field_type === 'combo';

    if (isSingleChoiceTextField) {
      const sel = String(conditionValue ?? '').trim();
      switch (condition.operator) {
        case 'checked':
          return sel.length > 0;
        case 'unchecked':
          return sel.length === 0;
        case 'equals':
          return sel.toLowerCase() === String(condition.value ?? '').toLowerCase().trim();
        case 'not_equals':
          return sel.toLowerCase() !== String(condition.value ?? '').toLowerCase().trim();
        default:
          return false;
      }
    }

    switch (condition.operator) {
      case 'checked':
        return conditionValue === true || conditionValue === 'true' || conditionValue === 1 || conditionValue === '1';
      case 'unchecked':
        return !(conditionValue === true || conditionValue === 'true' || conditionValue === 1 || conditionValue === '1');
      case 'equals':
        return String(conditionValue).toLowerCase().trim() === String(condition.value).toLowerCase().trim();
      case 'not_equals':
        return String(conditionValue).toLowerCase().trim() !== String(condition.value).toLowerCase().trim();
      default:
        return false;
    }
  };

  const getDateMinValidationForField = (
    field: WorkflowCustomField,
    values: { [fieldId: number]: any }
  ): { minDate: Date; message?: string } | null => {
    if (field.field_type !== 'date' || !field.validation_field_id || !field.validation_rules) {
      return null;
    }

    const validationField = findWorkflowFieldById(customFields, field.validation_field_id);
    const validationFieldValue = values[Number(field.validation_field_id)];
    if (isValidationSourceValueEmpty(validationFieldValue)) {
      return null;
    }

    const numericValidationValue = parseFieldNumericValue(
      validationFieldValue,
      validationField?.field_type
    );
    let bestDays: number | null = null;
    let bestMessage: string | undefined;

    try {
      const parsedRules = parseValidationRulesJson(field.validation_rules);
      if (!parsedRules?.rules?.length) {
        return null;
      }

      parsedRules.rules.forEach((rule: any) => {
        if (rule.operator === 'min_date_days') {
          const d = coerceMinDateDaysValue(rule.min_date_days);
          if (d !== null && (bestDays === null || d > bestDays)) {
            bestDays = d;
            bestMessage = undefined;
          }
          return;
        }

        if (rule.operator !== 'value_ranges' || !Array.isArray(rule.value_ranges) || isNaN(numericValidationValue)) {
          return;
        }

        const picked = pickValueRangeValidationForNumericValue(
          rule.value_ranges,
          numericValidationValue,
          evaluateConditionSatisfied,
          values
        );

        const effectiveValidation = picked?.validation;
        if (effectiveValidation?.type === 'min_date_days') {
          const days = coerceMinDateDaysValue(effectiveValidation.value);
          if (days !== null && (bestDays === null || days > bestDays)) {
            bestDays = days;
            bestMessage = effectiveValidation.message;
          }
        }
      });
    } catch {
      return null;
    }

    if (bestDays === null) {
      return null;
    }

    return { minDate: addDaysLocal(startOfTodayLocal(), bestDays), message: bestMessage };
  };

  /** Prazo mínimo já usado no prefill de cada campo de data. */
  const lastPrefillMinDateRef = useRef<{ [dateFieldId: number]: number }>({});

  // Reposiciona a sugestão (data mínima + 1 dia) sempre que um novo prazo passa a valer.
  // O usuário continua livre para escolher outra data depois, inclusive menor que o mínimo:
  // nesse caso aparece apenas o aviso, sem bloqueio.
  React.useEffect(() => {
    if (customFields.length === 0) return;

    const updates: { [fieldId: number]: any } = {};

    customFields.forEach((field) => {
      if (field.field_type !== 'date' || !field.validation_field_id) {
        return;
      }

      const minValidation = getDateMinValidationForField(field, customFieldValues);
      if (!minValidation) {
        delete lastPrefillMinDateRef.current[field.id];
        return;
      }

      const minDateTime = minValidation.minDate.getTime();
      const prefill = shouldPrefillDateField({
        currentValue: customFieldValues[field.id],
        minDateTime,
        lastPrefillMinDateTime: lastPrefillMinDateRef.current[field.id],
        triggeredByUserEdit: pendingDatePrefillRef.current.has(field.id),
      });
      lastPrefillMinDateRef.current[field.id] = minDateTime;
      pendingDatePrefillRef.current.delete(field.id);

      if (prefill) {
        updates[field.id] = formatDateInputLocal(addDaysLocal(minValidation.minDate, 1));
      }
    });

    if (Object.keys(updates).length > 0) {
      setCustomFieldValues((prev) => ({ ...prev, ...updates }));
    }
  }, [customFields, customFieldValues]);

  // Aviso (não bloqueia o envio) quando a data informada é anterior ao prazo mínimo da faixa.
  React.useEffect(() => {
    if (customFields.length === 0) return;

    const newErrors: { [fieldId: number]: string } = {};
    customFields.forEach((field) => {
      if (field.field_type !== 'date') return;

      const currentValue = customFieldValues[field.id];
      if (!currentValue) return;

      const minValidation = getDateMinValidationForField(field, customFieldValues);
      if (!minValidation) return;

      const enteredDate = parseDateInputLocal(currentValue);
      if (!enteredDate) return;

      if (enteredDate < minValidation.minDate) {
        newErrors[field.id] =
          minValidation.message ||
          `O campo "${field.label}" deve ser pelo menos até ${minValidation.minDate.toLocaleDateString('pt-BR')}.`;
      }
    });

    setCustomFieldInlineErrors(newErrors);
  }, [customFields, customFieldValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Proteção contra duplo clique - retornar se já estiver processando
    if (loading) {
      return;
    }
    
    // Validação de campos obrigatórios (mensagem com campo e aba conforme item 17 do backlog)
    const errors: string[] = [];
    
    const hasAnyCustomGroup = customFields.some((field) => {
      const isVisible = field.step_is_visible !== false;
      return isVisible && !!field.group_name?.trim();
    });
    
    const getCustomFieldTabName = (field: WorkflowCustomField): string => {
      const groupName = field.group_name?.trim();
      if (groupName) return groupName;
      return hasAnyCustomGroup ? 'Geral' : 'Campos Customizados';
    };
    
    const getRequiredFieldMessage = (fieldName: string, tabName?: string): string =>
      tabName
        ? `Preencha o campo "${fieldName}" na aba "${tabName}"`
        : `Preencha o campo "${fieldName}"`;

    const getApiErrorDetail = (error: any): string => {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        return detail
          .map((d: any) => {
            if (typeof d === 'object' && d?.msg) {
              return `${d.loc?.join('.') || ''}: ${d.msg}`;
            }
            return String(d);
          })
          .join('\n');
      }
      if (typeof detail === 'object' && detail !== null) {
        return JSON.stringify(detail);
      }
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      if (error?.message) {
        return String(error.message);
      }
      return 'Erro desconhecido';
    };

    customFields.forEach((field) => {
      if (field.field_type !== 'user') return;

      const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
      const isVisible = field.step_is_visible !== false;
      if (isBlocked || !isVisible) return;

      const userSearchTerm = (searchUsers[field.id] || '').trim();
      const selectedUserId = Number(customFieldValues[field.id]);
      const hasSelectedUser =
        Number.isFinite(selectedUserId) &&
        selectedUserId > 0 &&
        (users.some((u) => u.id === selectedUserId) ||
          pickedPurchaseCustomUsers[field.id]?.id === selectedUserId);

      if ((userSearchTerm && !hasSelectedUser) || (!userSearchTerm && customFieldValues[field.id] && !hasSelectedUser)) {
        const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
        errors.push(`Selecione um usuário cadastrado para o campo "${fieldLabel}" na aba "${getCustomFieldTabName(field)}"`);
      }
    });

    const effectiveRequesterId = requesterId ?? currentUser?.id ?? null;
    const requesterForSelfApprove =
      selectedRequester?.id === effectiveRequesterId
        ? selectedRequester
        : effectiveRequesterId === currentUser?.id
          ? currentUser
          : null;
    if (effectiveRequesterId && !userMayBeRequesterAndApprover(requesterForSelfApprove)) {
      customFields.forEach((field) => {
        if (field.field_type !== 'approver' && field.field_type !== 'contract_manager_approver') return;
        const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
        const isVisible = field.step_is_visible !== false;
        if (isBlocked || !isVisible) return;

        const selectedApproverId = Number(customFieldValues[field.id]);
        if (Number.isFinite(selectedApproverId) && selectedApproverId === effectiveRequesterId) {
          const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
          errors.push(
            `O requisitante não pode ser selecionado como aprovador no campo "${fieldLabel}" na aba "${getCustomFieldTabName(field)}"`
          );
        }
      });
    }
    
    if (!workflowId) {
      errors.push(getRequiredFieldMessage('Tipo de Contratação'));
    }
    
    if (!object || object.trim() === '') {
      errors.push(getRequiredFieldMessage('Objeto'));
    }
    
    if (!itemsDescription || itemsDescription.trim() === '') {
      errors.push(getRequiredFieldMessage('Descrição dos Itens'));
    }
    
    if (!groupId) {
      errors.push(getRequiredFieldMessage('Grupo para Encaminhar'));
    }

    if (!urgency) {
      errors.push(getRequiredFieldMessage('Urgência'));
    }
    
    // Validação de campos customizados obrigatórios
    customFields.forEach((field) => {
      const isRequired = !!field.is_required || !!field.step_is_required;
      const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
      const isVisible = field.step_is_visible !== false;
      
      // Item 38: obrigatoriedade só vale se a condição de exibição for atendida
      const shouldShow = shouldShowConditionalField(field, customFields, customFieldValues);
      const isSupplierField = isSupplierCustomField(field);

      if (!isBlocked && isVisible && shouldShow && isSupplierField) {
        const supplierRaw = customFieldValues[field.id];
        const supplierError = validateSupplierSelectionValue(
          supplierRaw,
          workflows.find((w: Workflow) => String(w.id) === workflowId)?.name,
          field.options
        );
        if (supplierError) {
          const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
          errors.push(`O campo "${fieldLabel}" ${supplierError}`);
        }
      }
      
      // Validação especial para platform_document
      // Validar tanto campos obrigatórios quanto opcionais quando um modo é selecionado
      if (field.field_type === 'platform_document' && !isBlocked && isVisible && shouldShow) {
        const mode = platformDocumentMode[field.id] || 'upload';
        const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
        const tabName = getCustomFieldTabName(field);
        
        // Validar se o campo está preenchido corretamente
        // Se o campo é obrigatório OU se um modo foi selecionado (mesmo que opcional)
        // Verificar se há um modo selecionado (não é o padrão 'upload' sem arquivo)
        const hasFile =
          !!customFieldFiles[field.id] || hasSavedCustomFieldFile(customFieldSavedFiles[field.id]);
        const hasSelectedDoc = !!platformDocumentSelectedIds[field.id];
        const hasSelectedMode = mode !== 'upload' || hasFile || hasSelectedDoc;
        
        // Se o campo é obrigatório OU se um modo foi selecionado, validar
        if (isRequired || hasSelectedMode) {
          if (mode === 'upload') {
            if (!hasFile) {
              const errorMsg = `É necessário fazer upload de um arquivo para o campo "${fieldLabel}"`;
              errors.push(getRequiredFieldMessage(fieldLabel, tabName));
              setCustomFieldInlineErrors(prev => ({ ...prev, [field.id]: errorMsg }));
            } else {
              // Limpar erro se arquivo foi selecionado
              setCustomFieldInlineErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field.id];
                return newErrors;
              });
            }
          } else if (mode === 'platform') {
            if (!hasSelectedDoc) {
              const errorMsg = `É necessário selecionar um documento da plataforma para o campo "${fieldLabel}"`;
              errors.push(getRequiredFieldMessage(fieldLabel, tabName));
              setCustomFieldInlineErrors(prev => ({ ...prev, [field.id]: errorMsg }));
            } else {
              // Limpar erro se documento foi selecionado
              setCustomFieldInlineErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field.id];
                return newErrors;
              });
            }
          } else if (mode === 'create') {
            // Se o usuário escolheu "Criar Novo Documento", ele precisa voltar e
            // selecionar um documento da plataforma antes de concluir a requisição.
            const errorMsg = `Após criar o documento, selecione-o em "Selecionar Documento da Plataforma" para o campo "${fieldLabel}"`;
            errors.push(getRequiredFieldMessage(fieldLabel, tabName));
            setCustomFieldInlineErrors(prev => ({ ...prev, [field.id]: errorMsg }));
          }
        } else {
          // Campo opcional e nenhum modo foi selecionado - limpar erros
          setCustomFieldInlineErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field.id];
            return newErrors;
          });
        }
      } else if (isRequired && !isBlocked && isVisible && shouldShow) {
        // Validação para outros tipos de campos (apenas obrigatórios)
        if (field.field_type === 'file' || field.field_type === 'files') {
          // Aceita arquivo novo em memória ou já persistido no rascunho
          const fieldFile = customFieldFiles[field.id];
          const hasSaved = hasSavedCustomFieldFile(customFieldSavedFiles[field.id]);
          if (!fieldFile && !hasSaved) {
            const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
            errors.push(getRequiredFieldMessage(fieldLabel, getCustomFieldTabName(field)));
          }
        } else {
          const fieldValue = customFieldValues[field.id];
          const isEmpty = fieldValue === undefined || 
                          fieldValue === null || 
                          fieldValue === '' ||
                          (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                          (field.field_type === 'table' && !hasTableContent(fieldValue)) ||
                          (isSupplierField && getSupplierSelectionCount(fieldValue) === 0);
          
          if (isEmpty) {
            const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
            errors.push(getRequiredFieldMessage(fieldLabel, getCustomFieldTabName(field)));
          }
        }
      }
      
      // Validação condicional
      if (field.validation_field_id && field.validation_rules) {
        const validationFieldValue = customFieldValues[Number(field.validation_field_id)];
        const currentFieldValue = customFieldValues[field.id];
        
        if (
          !isValidationSourceValueEmpty(validationFieldValue) &&
          currentFieldValue !== undefined &&
          currentFieldValue !== null &&
          currentFieldValue !== ''
        ) {
          try {
            const rules = parseValidationRulesJson(field.validation_rules);
            if (rules?.rules && Array.isArray(rules.rules)) {
              // Validar cada regra (todas devem ser satisfeitas - AND)
              rules.rules.forEach((rule: any) => {
                const effectiveRuleFieldId =
                  rule.field_id !== undefined && rule.field_id !== null && rule.field_id !== ''
                    ? Number(rule.field_id)
                    : field.validation_field_id;
                const ruleField = findWorkflowFieldById(customFields, effectiveRuleFieldId);
                if (!ruleField) return;
                
                let isValid = true;
                let errorMessage = '';
                
                // Aplicar operador de comparação
                switch (rule.operator) {
                  case 'equals':
                    isValid = String(validationFieldValue).toLowerCase().trim() === String(rule.value).toLowerCase().trim();
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser igual a "${rule.value}"`;
                    }
                    break;
                  
                  case 'not_equals':
                    isValid = String(validationFieldValue).toLowerCase().trim() !== String(rule.value).toLowerCase().trim();
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" não deve ser igual a "${rule.value}"`;
                    }
                    break;
                  
                  case 'contains':
                    isValid = String(validationFieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve conter "${rule.value}"`;
                    }
                    break;
                  
                  case 'greater_than':
                    const val1 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal1 = parseFloat(String(rule.value));
                    isValid = !isNaN(val1) && !isNaN(ruleVal1) && val1 > ruleVal1;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser maior que ${rule.value}`;
                    }
                    break;
                  
                  case 'greater_equal':
                    const val2 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal2 = parseFloat(String(rule.value));
                    isValid = !isNaN(val2) && !isNaN(ruleVal2) && val2 >= ruleVal2;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser maior ou igual a ${rule.value}`;
                    }
                    break;
                  
                  case 'less_than':
                    const val3 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal3 = parseFloat(String(rule.value));
                    isValid = !isNaN(val3) && !isNaN(ruleVal3) && val3 < ruleVal3;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser menor que ${rule.value}`;
                    }
                    break;
                  
                  case 'less_equal':
                    const val4 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal4 = parseFloat(String(rule.value));
                    isValid = !isNaN(val4) && !isNaN(ruleVal4) && val4 <= ruleVal4;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser menor ou igual a ${rule.value}`;
                    }
                    break;
                  
                  case 'min_date_days':
                    // Prazo mínimo é apenas orientativo: o aviso já aparece no campo e o envio segue liberado.
                    break;
                  
                  case 'value_ranges':
                    // Validação por faixas de valor
                    if (rule.value_ranges && Array.isArray(rule.value_ranges)) {
                      const validationValue = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                      if (!isNaN(validationValue)) {
                        const picked = pickValueRangeValidationForNumericValue(
                          rule.value_ranges,
                          validationValue,
                          evaluateConditionSatisfied,
                          customFieldValues
                        );
                        const applicableRange = picked ? { validation: picked.validation } : null;

                        // Aplicar validação baseada na faixa encontrada (mais restritiva se houver sobreposição)
                        if (applicableRange && applicableRange.validation) {
                          const validation = applicableRange.validation;
                          isValid = true;
                          
                          switch (validation.type) {
                            case 'min_date_days':
                              // Prazo mínimo é apenas orientativo: o aviso já aparece no campo e o envio segue liberado.
                              break;

                              
                            case 'min_value':
                              if (typeof validation.value === 'number') {
                                const currentValue = parseFloat(String(currentFieldValue));
                                isValid = !isNaN(currentValue) && currentValue >= validation.value;
                                if (!isValid) {
                                  errorMessage = validation.message || 
                                    `O campo "${field.label}" deve ser maior ou igual a ${validation.value}`;
                                }
                              }
                              break;
                              
                            case 'max_value':
                              if (typeof validation.value === 'number') {
                                const currentValue = parseFloat(String(currentFieldValue));
                                isValid = !isNaN(currentValue) && currentValue <= validation.value;
                                if (!isValid) {
                                  errorMessage = validation.message || 
                                    `O campo "${field.label}" deve ser menor ou igual a ${validation.value}`;
                                }
                              }
                              break;
                              
                            case 'equals':
                              isValid = String(currentFieldValue).toLowerCase().trim() === String(validation.value).toLowerCase().trim();
                              if (!isValid) {
                                errorMessage = validation.message || 
                                  `O campo "${field.label}" deve ser igual a "${validation.value}"`;
                              }
                              break;
                          }
                        } else {
                          isValid = false;
                          errorMessage = `Nenhuma faixa de valor aplicável encontrada para o valor ${validationValue}`;
                        }
                      }
                    }
                    break;
                  
                  default:
                    // Formato antigo: compatibilidade com regras antigas
                    if (rule.value_range) {
                      const validationValue = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                      if (!isNaN(validationValue)) {
                        const min = rule.value_range.min ?? -Infinity;
                        const max = rule.value_range.max ?? Infinity;
                        isValid = validationValue >= min && (max === null || validationValue <= max);
                        // rule.min_date_days não é checado aqui: prazo mínimo é apenas orientativo.
                      }
                    }
                }
                
                if (!isValid && errorMessage) {
                  errors.push(errorMessage);
                }
              });
            }
          } catch (error) {
            console.error('Erro ao validar campo condicional:', error);
          }
        }
      }
    });
    
    // Se houver erros, exibir mensagem
    if (errors.length > 0) {
      showAlert({
        title: 'Campos Obrigatórios Não Preenchidos',
        message: 'Revise os campos pendentes abaixo:',
        variant: 'warning',
        details: errors.map(error => `• ${error}`).join('\n')
      });
      return;
    }
    
    setLoading(true);

    try {
      // Criar a requisição de compra
      let purchaseRequestId: number;
      // Gravação adiada dos campos de aprovação: eles movem a requisição para
      // "aguardando aprovação", e a partir daí o backend recusa qualquer escrita
      // do requisitante — inclusive os uploads de anexo.
      let flushApprovalFieldValues: () => Promise<void> = async () => {};
      
      // Se for um rascunho, atualizar e salvar campos customizados ANTES de finalizar
      if (isDraft && draftId) {
        // Atualizar o rascunho com os dados finais antes de finalizar
        const draftUpdatePayload: Record<string, unknown> = {
          title: object,
          object,
          items_description: itemsDescription,
          urgency,
          workflow_id: parseInt(workflowId),
          assigned_group_id: parseInt(groupId),
          requester_id: currentUser?.id ?? null,
          status: PURCHASE_DRAFT_STATUS,
        };
        if (canManagePriority) {
          draftUpdatePayload.priority = priority;
        }
        try {
          await axios.put(purchaseDraftItemUrl(API_URL, draftId), draftUpdatePayload);
        } catch (draftUpdateError) {
          throwNotADraftForSubmit(draftUpdateError);
        }
        
        // Salvar valores dos campos customizados ANTES de finalizar (para que process_custom_field_approvers funcione)
        // Usar draftId para salvar os campos customizados
        const eligibleDraftCustomFields = customFields
        .filter(field => {
          const fieldValue = customFieldValues[field.id];
          const mode = platformDocumentMode[field.id] || 'upload';
          // Incluir platform_document apenas no modo 'platform' com documento selecionado
          // No modo 'upload', o registro será criado diretamente pelo endpoint de upload
          if (field.field_type === 'platform_document') {
            if (mode === 'platform') {
              // Verificar se há um documento selecionado em platformDocumentSelectedIds
              const docId = platformDocumentSelectedIds[field.id];
              return docId !== undefined && docId !== null;
            } else if (mode === 'upload') {
              // Não incluir no modo upload - o upload criará o registro diretamente
              return false;
            }
            return false;
          }
          if (field.field_type === 'check' && !field.allow_multiple) {
            return hasCheckFieldPersistedValue(field, fieldValue);
          }
          return fieldValue !== undefined && fieldValue !== '' && fieldValue !== null && 
                 !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                 !(field.field_type === 'table' && !hasTableContent(fieldValue)) &&
                 field.field_type !== 'file' && field.field_type !== 'files'; // Arquivos são tratados separadamente
        });

        const saveDraftCustomFieldValue = (field: (typeof eligibleDraftCustomFields)[number]) => {
          const fieldValue = customFieldValues[field.id];
          const mode = platformDocumentMode[field.id] || 'upload';
          
          // Validação: verificar se o campo tem valor válido
          if (field.field_type === 'check' && !field.allow_multiple) {
            if (!hasCheckFieldPersistedValue(field, fieldValue)) {
              return Promise.resolve(null);
            }
          } else if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
            // Pular campos vazios (já filtrados, mas garantir)
            return Promise.resolve(null);
          }
          
          // Validação adicional para arrays vazios
          if (Array.isArray(fieldValue) && fieldValue.length === 0) {
            return Promise.resolve(null);
          }
          
          const valueData: any = {
            purchase_request_id: draftId,
            custom_field_id: field.id,
          };
          
          // Tratar campo platform_document no modo 'platform'
          if (field.field_type === 'platform_document' && mode === 'platform') {
            const docId = platformDocumentSelectedIds[field.id];
            if (docId) {
              valueData.document_instance_id = docId;
              valueData.text_value = null;
              valueData.file_path = null;
              valueData.file_paths = null;
            } else {
              // Se não há docId, não salvar
              return Promise.resolve(null);
            }
          } else if (field.field_type === 'platform_document' && mode === 'upload') {
            // No modo upload, não criar registro aqui - o upload criará o registro diretamente
            return Promise.resolve(null);
          } else if (field.field_type === 'table') {
            valueData.text_value = serializeTableValue(fieldValue);
          } else if (isSupplierCustomField(field)) {
            const supplierSerialized = normalizeSupplierFieldRawValue(fieldValue);
            if (!supplierSerialized) {
              return Promise.resolve(null);
            }
            valueData.text_value = supplierSerialized;
          } else if (field.field_type === 'currency' || field.field_type === 'period') {
            // Campos que são salvos como JSON - validar que o valor pode ser serializado
            try {
              valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
            } catch (e) {
              console.error(`Erro ao serializar campo ${field.id} (${field.field_type}):`, e);
              return Promise.resolve(null);
            }
          } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
            // Campos de lista/combo/check com múltiplas opções
            try {
              valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
                ? JSON.stringify(fieldValue)
                : String(fieldValue);
            } catch (e) {
              console.error(`Erro ao serializar campo ${field.id} (${field.field_type}):`, e);
              return Promise.resolve(null);
            }
          } else if (field.field_type === 'check' && !field.allow_multiple) {
            const checkSerialized = serializeCheckFieldValue(field, fieldValue);
            if (!checkSerialized) {
              return Promise.resolve(null);
            }
            valueData.text_value = checkSerialized;
          } else {
            // Campos de texto, número, data, link, user, analyst, etc.
            // Validar que o valor não é apenas espaços em branco
            const stringValue = String(fieldValue).trim();
            if (stringValue === '' || stringValue === '[object Object]') {
              return Promise.resolve(null);
            }
            valueData.text_value = stringValue;
          }
          
          return upsertPurchaseCustomFieldValue(API_URL, valueData).catch((error: unknown) => {
            const createDetail = getApiErrorDetail(error);
            console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
            throw new Error(`Campo "${field.label}": ${createDetail}`);
          });
        };
      
        // Campos de aprovação por último: gravá-los antes coloca a requisição em
        // "aguardando aprovação" e o backend passa a recusar os campos restantes.
        const draftSaveWaves = partitionFieldsForSave(eligibleDraftCustomFields);
        await Promise.all(draftSaveWaves.regularFields.map(saveDraftCustomFieldValue));
        await Promise.all(draftSaveWaves.approvalFields.map(saveDraftCustomFieldValue));
        
        // Pequeno delay para garantir que os campos foram salvos no banco antes de finalizar
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const finalizeResponse = await axios.post(purchaseDraftFinalizeUrl(API_URL, draftId));
        purchaseRequestId = finalizeResponse.data.id;
        
        // Limpar rascunho do localStorage
        if (currentUser) {
          localStorage.removeItem(`purchase_draft_${currentUser.id}`);
        }
      } else {
        // Criar nova requisição
        const createPayload: Record<string, unknown> = {
          title: object,
          object,
          items_description: itemsDescription,
          description: itemsDescription,
          urgency,
          workflow_id: parseInt(workflowId),
          assigned_group_id: parseInt(groupId),
          requester_id: currentUser?.id ?? null,
        };
        if (canManagePriority) {
          createPayload.priority = priority;
        }
        const purchaseRequestResponse = await axios.post(`${API_URL}/api/purchases/`, createPayload);
        purchaseRequestId = purchaseRequestResponse.data.id;
        
        // Limpar rascunho do localStorage se houver
        if (currentUser) {
          localStorage.removeItem(`purchase_draft_${currentUser.id}`);
        }
        
        // Salvar valores dos campos customizados em paralelo (muito mais rápido)
        const eligibleCustomFields = customFields
        .filter(field => {
          const fieldValue = customFieldValues[field.id];
          const mode = platformDocumentMode[field.id] || 'upload';
          // Incluir platform_document apenas no modo 'platform' com documento selecionado
          // No modo 'upload', o registro será criado diretamente pelo endpoint de upload
          if (field.field_type === 'platform_document') {
            if (mode === 'platform') {
              // Verificar se há um documento selecionado em platformDocumentSelectedIds
              const docId = platformDocumentSelectedIds[field.id];
              return docId !== undefined && docId !== null;
            } else if (mode === 'upload') {
              // Não incluir no modo upload - o upload criará o registro diretamente
              return false;
            }
            return false;
          }
          if (field.field_type === 'check' && !field.allow_multiple) {
            return hasCheckFieldPersistedValue(field, fieldValue);
          }
          return fieldValue !== undefined && fieldValue !== '' && fieldValue !== null && 
                 !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                 !(field.field_type === 'table' && !hasTableContent(fieldValue)) &&
                 field.field_type !== 'file' && field.field_type !== 'files'; // Arquivos são tratados separadamente
        });

        const saveCustomFieldValue = (field: (typeof eligibleCustomFields)[number]) => {
          const fieldValue = customFieldValues[field.id];
          const mode = platformDocumentMode[field.id] || 'upload';
          
          // Validação: verificar se o campo tem valor válido
          if (field.field_type === 'check' && !field.allow_multiple) {
            if (!hasCheckFieldPersistedValue(field, fieldValue)) {
              return Promise.resolve(null);
            }
          } else if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
            // Pular campos vazios (já filtrados, mas garantir)
            return Promise.resolve(null);
          }
          
          // Validação adicional para arrays vazios
          if (Array.isArray(fieldValue) && fieldValue.length === 0) {
            return Promise.resolve(null);
          }
          
          const valueData: any = {
            purchase_request_id: purchaseRequestId,
            custom_field_id: field.id,
          };
          
          // Tratar campo platform_document no modo 'platform'
          if (field.field_type === 'platform_document' && mode === 'platform') {
            const docId = platformDocumentSelectedIds[field.id];
            if (docId) {
              valueData.document_instance_id = docId;
              valueData.text_value = null;
              valueData.file_path = null;
              valueData.file_paths = null;
            } else {
              // Se não há docId, não salvar
              return Promise.resolve(null);
            }
          } else if (field.field_type === 'platform_document' && mode === 'upload') {
            // No modo upload, não criar registro aqui - o upload criará o registro diretamente
            return Promise.resolve(null);
          } else if (field.field_type === 'table') {
            valueData.text_value = serializeTableValue(fieldValue);
          } else if (isSupplierCustomField(field)) {
            const supplierSerialized = normalizeSupplierFieldRawValue(fieldValue);
            if (!supplierSerialized) {
              return Promise.resolve(null);
            }
            valueData.text_value = supplierSerialized;
          } else if (field.field_type === 'currency' || field.field_type === 'period') {
            // Campos que são salvos como JSON - validar que o valor pode ser serializado
            try {
              valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
            } catch (e) {
              console.error(`Erro ao serializar campo ${field.id} (${field.field_type}):`, e);
              return Promise.resolve(null);
            }
          } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
            // Campos de lista/combo/check com múltiplas opções
            try {
              valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
                ? JSON.stringify(fieldValue)
                : String(fieldValue);
            } catch (e) {
              console.error(`Erro ao serializar campo ${field.id} (${field.field_type}):`, e);
              return Promise.resolve(null);
            }
          } else if (field.field_type === 'check' && !field.allow_multiple) {
            const checkSerialized = serializeCheckFieldValue(field, fieldValue);
            if (!checkSerialized) {
              return Promise.resolve(null);
            }
            valueData.text_value = checkSerialized;
          } else {
            // Campos de texto, número, data, link, user, analyst, etc.
            // Validar que o valor não é apenas espaços em branco
            const stringValue = String(fieldValue).trim();
            if (stringValue === '' || stringValue === '[object Object]') {
              return Promise.resolve(null);
            }
            valueData.text_value = stringValue;
          }
          
          return upsertPurchaseCustomFieldValue(API_URL, valueData).catch((error: unknown) => {
            const createDetail = getApiErrorDetail(error);
            console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
            throw new Error(`Campo "${field.label}": ${createDetail}`);
          });
        };
      
        const saveWaves = partitionFieldsForSave(eligibleCustomFields);
        await Promise.all(saveWaves.regularFields.map(saveCustomFieldValue));
        flushApprovalFieldValues = async () => {
          if (saveWaves.approvalFields.length === 0) {
            return;
          }
          // Todos os aprovadores em uma chamada: a API processa as aprovações uma única
          // vez no fim, então um segundo campo aprovador não é recusado.
          const approvalItems = saveWaves.approvalFields
            .map((field) => ({
              custom_field_id: field.id,
              text_value: String(customFieldValues[field.id]),
            }))
            .filter((item) => item.text_value && item.text_value !== 'undefined');
          try {
            const batchResult = await batchUpsertPurchaseCustomFieldValues(
              API_URL,
              purchaseRequestId,
              approvalItems
            );
            if (batchResult.errors.length > 0) {
              throw new Error(
                batchResult.errors
                  .map((batchError) => {
                    const field = saveWaves.approvalFields.find(
                      (item) => item.id === batchError.custom_field_id
                    );
                    const label = field?.label?.trim() || `ID ${batchError.custom_field_id}`;
                    return `Campo "${label}": ${batchError.detail}`;
                  })
                  .join('\n')
              );
            }
            return;
          } catch (error: unknown) {
            if (!isBatchEndpointUnavailable(error)) {
              throw error;
            }
          }
          await Promise.all(saveWaves.approvalFields.map(saveCustomFieldValue));
        };
        
        // Pequeno delay para garantir que os campos foram salvos no banco antes dos uploads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Upload apenas de arquivos novos em memória (já persistidos no rascunho são preservados no finalize)
      const uploadResult = await uploadPurchaseCustomFieldFiles({
        apiUrl: API_URL,
        purchaseRequestId,
        customFields,
        customFieldFiles,
        platformDocumentMode,
      });
      if (uploadResult.errors.length > 0) {
        throw new Error(
          `Falha ao enviar ${uploadResult.errors.length} arquivo(s):\n${uploadResult.errors.join('\n')}`
        );
      }

      await flushApprovalFieldValues();
      
      // Limpar campos primeiro
      setObject('');
      setItemsDescription('');
      setUrgency('');
      setPriority('p4');
      setWorkflowId('');
      setGroupId('');
      setCustomFields([]);
      setCustomFieldValues({});
      setCustomFieldFiles({});
      setCustomFieldSavedFiles({});
      setPlatformDocumentMode({});
      setPlatformDocumentSelectedIds({});
      setDraftId(null);
      setIsDraft(false);
      setRequesterId(null);
      setRequesterSearchTerm('');
      setDebouncedRequesterQuery('');
      setSelectedRequester(null);
      setFilteredRequesterUsers([]);
      
      // Atualizar lista de requisições de compras
      onSuccess();
      
      // Mostrar confirmação primeiro
      showAlert({
        title: 'Sucesso',
        message: 'Requisição de compra criada com sucesso!',
        variant: 'success'
      });
      
      // Fechar modal após um delay para garantir que o alerta seja exibido
      setTimeout(() => {
        onHide();
      }, 300);
    } catch (error: any) {
      console.error('Erro ao criar requisição de compra:', error);
      let errorDetails = 'Erro desconhecido';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorDetails = detail.map((d: any) => `${d.loc?.join('.') || ''}: ${d.msg || ''}`).join('\n');
        } else if (typeof detail === 'object') {
          errorDetails = JSON.stringify(detail);
        } else {
          errorDetails = String(detail);
        }
      } else if (error?.message) {
        errorDetails = String(error.message);
      }
      showAlert({
        title: 'Erro ao Criar Requisição de Compra',
        message: 'Não foi possível criar a requisição de compra.',
        variant: 'danger',
        details: errorDetails
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Nova Requisição de Compra</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className="mb-3">
            <Form.Label>Objeto <span className="text-danger">*</span></Form.Label>
            <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
              {PURCHASE_OBJECT_HELP_TEXT}
            </Form.Text>
            <Form.Control
              as="textarea"
              rows={3}
              value={object}
              onChange={(e) => setObject(e.target.value)}
              required
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição dos Itens <span className="text-danger">*</span></Form.Label>
            <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
              {PURCHASE_ITEMS_HELP_TEXT}
            </Form.Text>
            <Form.Control
              as="textarea"
              rows={6}
              value={itemsDescription}
              onChange={(e) => setItemsDescription(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tipo de Contratação <span className="text-danger">*</span></Form.Label>
            <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
              {PURCHASE_WORKFLOW_HELP_TEXT}
            </Form.Text>
            <Form.Select 
              value={workflowId} 
              onChange={(e) => setWorkflowId(e.target.value)}
              required
            >
              <option value="">Selecione um tipo</option>
              {workflows
                .filter((w: Workflow) => w.is_active)
                .sort((a: Workflow, b: Workflow) => a.name.localeCompare(b.name, 'pt-BR'))
                .map((workflow: Workflow) => (
                  <option key={workflow.id} value={String(workflow.id)}>
                    {workflow.name}
                  </option>
                ))}
            </Form.Select>
            {workflowId && (() => {
              const selectedWorkflow = workflows.find((w: Workflow) => String(w.id) === workflowId);
              return selectedWorkflow ? (
                <div className="mt-2 p-3 workflow-info-box rounded">
                  <strong className="workflow-info-title">Tipo Selecionado:</strong> {selectedWorkflow.name}
                  {selectedWorkflow.description && (
                    <div className="mt-2">
                      <small className="workflow-info-description">{selectedWorkflow.description}</small>
                    </div>
                  )}
                </div>
              ) : null;
            })()}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Urgência <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as TicketUrgency)}
              required
              disabled={!workflowId}
              aria-label="Selecione a urgência da requisição"
            >
              <option value="" disabled>Selecione a urgência</option>
              {URGENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Informe o nível de urgência da sua solicitação.
            </Form.Text>
          </Form.Group>

          {canManagePriority && (
            <Form.Group className="mb-3">
              <Form.Label>Prioridade</Form.Label>
              <Form.Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                disabled={!workflowId}
              >
                <option value="p1">P1</option>
                <option value="p2">P2</option>
                <option value="p3">P3</option>
                <option value="p4">P4</option>
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Solicitante</Form.Label>
            <Form.Control
              type="text"
              value={currentUser?.email || ''}
              disabled
              readOnly
              aria-label="Solicitante (usuário logado)"
            />
            <Form.Text className="text-muted">
              O solicitante é sempre o usuário logado e não pode ser alterado.
            </Form.Text>
          </Form.Group>

          {/* Campos Customizados */}
          {customFields.length > 0 ? (() => {
            const shouldShowField = (field: WorkflowCustomField): boolean =>
              shouldShowConditionalField(field, customFields, customFieldValues);

            // Agrupar campos por group_name
            const groupedFields: { [key: string]: WorkflowCustomField[] } = {};
            const ungroupedFields: WorkflowCustomField[] = [];

            customFields.forEach(field => {
              // Respeitar a configuração da primeira etapa
              const isVisible = field.step_is_visible !== false;
              if (!isVisible) return; // Não renderizar campos invisíveis
              
              // Verificar condições condicionais
              if (!shouldShowField(field)) return; // Não renderizar campos que não atendem condições
              
              if (field.group_name) {
                const groupName = field.group_name;
                if (!groupedFields[groupName]) {
                  groupedFields[groupName] = [];
                }
                groupedFields[groupName].push(field);
              } else {
                ungroupedFields.push(field);
              }
            });

            const hasGroups = Object.keys(groupedFields).length > 0;
            const hasUngrouped = ungroupedFields.length > 0;

            // Função para renderizar um campo
            const renderField = (field: WorkflowCustomField) => {
              const isRequired = !!field.is_required || !!field.step_is_required;
              // Determinar se o campo está bloqueado: usar step_is_blocked se disponível
              const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
              const isSupplierField = isSupplierCustomField(field);
              const fieldValue = customFieldValues[field.id];
              const userSearchTerm = searchUsers[field.id] || '';
              const selectedWorkflowName = workflows.find((w: Workflow) => String(w.id) === workflowId)?.name;

              const bumpSuggestAnchor = (fieldId: number, el: HTMLElement | null) => {
                if (!el) return;
                const r = el.getBoundingClientRect();
                setCustomFieldSuggestAnchor((p) => ({
                  ...p,
                  [fieldId]: { top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) },
                }));
              };
              
              return (
                <Form.Group key={field.id} className="mb-3">
                  <Form.Label>
                    {field.label}
                    {isRequired && <span className="text-danger ms-1">*</span>}
                  </Form.Label>
                  {field.description && (
                    <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
                      {field.description}
                    </Form.Text>
                  )}
                  {isSupplierField ? (
                    <SupplierFieldControl
                      value={normalizeSupplierFieldRawValue(fieldValue)}
                      onChange={(nextSerializedValue) => handleCustomFieldChange(field.id, nextSerializedValue)}
                      disabled={isBlocked}
                      required={isRequired && !isBlocked}
                      workflowName={selectedWorkflowName}
                      fieldOptions={field.options}
                    />
                  ) : field.field_type === 'text_short' ? (
                    <Form.Control
                      type="text"
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                    />
                  ) : field.field_type === 'text_long' ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                    />
                  ) : field.field_type === 'list' || field.field_type === 'combo' ? (
                    (() => {
                      const options: string[] = field.options 
                        ? field.options.split(/[,\n]/).map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0)
                        : [];
                      if (field.allow_multiple) {
                        const selectedValues: string[] = Array.isArray(fieldValue) 
                          ? (fieldValue as string[])
                          : [];
                        return (
                          <div>
                            {options.map((option: string, idx: number) => {
                              const isChecked = selectedValues.includes(option);
                              return (
                                <Form.Check
                                  key={idx}
                                  type="checkbox"
                                  label={option}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const newValues: string[] = e.target.checked
                                      ? [...selectedValues, option]
                                      : selectedValues.filter((v: string) => v !== option);
                                    handleCustomFieldChange(field.id, newValues);
                                  }}
                                  disabled={isBlocked}
                                />
                              );
                            })}
                          </div>
                        );
                      } else {
                        const stringValue = fieldValue !== undefined && fieldValue !== null 
                          ? String(fieldValue) 
                          : '';
                        return (
                          <Form.Select
                            value={stringValue}
                            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                          >
                            <option value="">Selecione...</option>
                            {options.map((option: string, idx: number) => (
                              <option key={idx} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Select>
                        );
                      }
                    })()
                  ) : field.field_type === 'date' ? (
                    <div>
                      <Form.Control
                        type="date"
                        value={fieldValue || ''}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        required={isRequired && !isBlocked}
                        disabled={isBlocked}
                        readOnly={isBlocked}
                      />
                      {customFieldInlineErrors[field.id] && (
                        <Form.Text className="text-warning d-block mt-1">
                          {customFieldInlineErrors[field.id]}
                        </Form.Text>
                      )}
                    </div>
                  ) : field.field_type === 'period' ? (
                    (() => {
                      let periodValue = { start: '', end: '' };
                      if (fieldValue) {
                        try {
                          periodValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
                        } catch {
                          periodValue = { start: fieldValue || '', end: '' };
                        }
                      }
                      return (
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="date"
                            placeholder="Data Início"
                            value={periodValue.start || ''}
                            onChange={(e) => {
                              const newPeriod = { ...periodValue, start: e.target.value };
                              handleCustomFieldChange(field.id, JSON.stringify(newPeriod));
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                          />
                          <Form.Control
                            type="date"
                            placeholder="Data Fim"
                            value={periodValue.end || ''}
                            onChange={(e) => {
                              const newPeriod = { ...periodValue, end: e.target.value };
                              handleCustomFieldChange(field.id, JSON.stringify(newPeriod));
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                          />
                        </div>
                      );
                    })()
                  ) : field.field_type === 'link' ? (
                    <Form.Control
                      type="url"
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                      placeholder="https://example.com"
                    />
                  ) : field.field_type === 'user' ? (
                    (() => {
                      const selectedId = parseInt(fieldValue || '0', 10);
                      const remoteList = purchaseUserPickerResults[field.id] || [];
                      const filteredUserList = remoteList;
                      const selectedUser =
                        (pickedPurchaseCustomUsers[field.id]?.id === selectedId
                          ? pickedPurchaseCustomUsers[field.id]
                          : undefined) ||
                        users.find((u) => u.id === selectedId) ||
                        remoteList.find((u) => u.id === selectedId);

                      const isInvalidUserInput = !!userSearchTerm.trim() && !selectedUser;
                      const suggestAnchor = customFieldSuggestAnchor[field.id];
                      const showUserSuggest =
                        !!userSearchTerm && !selectedUser && !!suggestAnchor && filteredUserList.length > 0;

                      return (
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            data-custom-suggest-input={field.id}
                            value={selectedUser ? `${selectedUser.name} (${selectedUser.email})` : userSearchTerm}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSearchUsers({ ...searchUsers, [field.id]: v });
                              bumpSuggestAnchor(field.id, e.currentTarget);
                              if (!v) {
                                handleCustomFieldChange(field.id, '');
                                setPurchaseUserPickerResults((p) => ({ ...p, [field.id]: [] }));
                                setPickedPurchaseCustomUsers((p) => {
                                  const n = { ...p };
                                  delete n[field.id];
                                  return n;
                                });
                                setCustomFieldSuggestAnchor((p) => {
                                  const n = { ...p };
                                  delete n[field.id];
                                  return n;
                                });
                                const prevT = purchaseUserPickerTimersRef.current[field.id];
                                if (prevT) clearTimeout(prevT);
                                return;
                              }
                              schedulePurchaseUserPickerQuery(field.id, v);
                            }}
                            onFocus={(e) => {
                              bumpSuggestAnchor(field.id, e.currentTarget);
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                            isInvalid={isInvalidUserInput}
                            placeholder="Digite o nome ou email do usuário"
                          />
                          {showUserSuggest && suggestAnchor && (
                            <div
                              style={{
                                position: 'fixed',
                                top: suggestAnchor.top,
                                left: suggestAnchor.left,
                                width: suggestAnchor.width,
                                zIndex: 1080,
                                backgroundColor: 'white',
                                border: '1px solid #ced4da',
                                borderRadius: '0 0 0.375rem 0.375rem',
                                maxHeight: '220px',
                                overflowY: 'auto',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              }}
                            >
                              {filteredUserList.map((user) => (
                                <div
                                  key={user.id}
                                  onClick={() => {
                                    handleCustomFieldChange(field.id, user.id.toString());
                                    setSearchUsers({ ...searchUsers, [field.id]: '' });
                                    setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                                    setCustomFieldSuggestAnchor((p) => {
                                      const n = { ...p };
                                      delete n[field.id];
                                      return n;
                                    });
                                    setPurchaseUserPickerResults((p) => ({ ...p, [field.id]: [] }));
                                    setPickedPurchaseCustomUsers((p) => ({ ...p, [field.id]: user }));
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #e9ecef',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }}
                                >
                                  {user.name} ({user.email})
                                </div>
                              ))}
                            </div>
                          )}
                          {isInvalidUserInput && (
                            <Form.Text className="text-danger d-block mt-1">
                              Selecione um usuário cadastrado na lista.
                            </Form.Text>
                          )}
                        </div>
                      );
                    })()
                  ) : (field.field_type === 'approver' || field.field_type === 'contract_manager_approver') ? (
                    (() => {
                      const approverSearchTerm = searchUsers[field.id] || '';
                      const approverUsers = users.filter(
                        (u) =>
                          u.is_active &&
                          (field.field_type === 'contract_manager_approver' || !!u.can_approve)
                      );
                      const filteredApproverList = approverSearchTerm
                        ? approverUsers.filter(
                            (u) =>
                              u.name?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
                              u.email?.toLowerCase().includes(approverSearchTerm.toLowerCase())
                          ).slice(0, 10)
                        : [];
                      const selectedApprover = approverUsers.find((u) => u.id === parseInt(fieldValue || '0'));
                      const suggestAnchorApp = customFieldSuggestAnchor[field.id];
                      const showApproverSuggest =
                        !!approverSearchTerm &&
                        !selectedApprover &&
                        !!suggestAnchorApp &&
                        filteredApproverList.length > 0;

                      return (
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            data-custom-suggest-input={field.id}
                            value={
                              selectedApprover
                                ? `${selectedApprover.name} (${selectedApprover.email})`
                                : approverSearchTerm
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              setSearchUsers({ ...searchUsers, [field.id]: v });
                              bumpSuggestAnchor(field.id, e.currentTarget);
                              if (!v) {
                                handleCustomFieldChange(field.id, '');
                                setCustomFieldSuggestAnchor((p) => {
                                  const n = { ...p };
                                  delete n[field.id];
                                  return n;
                                });
                              }
                            }}
                            onFocus={(e) => {
                              bumpSuggestAnchor(field.id, e.currentTarget);
                              if (!approverSearchTerm) {
                                setFilteredUsers({ ...filteredUsers, [field.id]: approverUsers.slice(0, 10) });
                              }
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                            placeholder="Digite o nome ou email do aprovador"
                          />
                          {showApproverSuggest && suggestAnchorApp && (
                            <div
                              style={{
                                position: 'fixed',
                                top: suggestAnchorApp.top,
                                left: suggestAnchorApp.left,
                                width: suggestAnchorApp.width,
                                zIndex: 1080,
                                backgroundColor: 'white',
                                border: '1px solid #ced4da',
                                borderRadius: '0 0 0.375rem 0.375rem',
                                maxHeight: '220px',
                                overflowY: 'auto',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              }}
                            >
                              {filteredApproverList.map((user) => (
                                <div
                                  key={user.id}
                                  onClick={() => {
                                    handleCustomFieldChange(field.id, user.id.toString());
                                    setSearchUsers({ ...searchUsers, [field.id]: '' });
                                    setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                                    setCustomFieldSuggestAnchor((p) => {
                                      const n = { ...p };
                                      delete n[field.id];
                                      return n;
                                    });
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #e9ecef',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }}
                                >
                                  {user.name} ({user.email})
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : field.field_type === 'analyst' ? (
                    (() => {
                      const analystSearchTerm = searchUsers[field.id] || '';
                      const filteredAnalystList = analystSearchTerm
                        ? analystUsers.filter(
                            (u) =>
                              u.name?.toLowerCase().includes(analystSearchTerm.toLowerCase()) ||
                              u.email?.toLowerCase().includes(analystSearchTerm.toLowerCase())
                          ).slice(0, 10)
                        : [];
                      const selectedAnalyst = analystUsers.find((u) => u.id === parseInt(fieldValue || '0'));
                      const suggestAnchorAn = customFieldSuggestAnchor[field.id];
                      const showAnalystSuggest =
                        !!analystSearchTerm &&
                        !selectedAnalyst &&
                        !!suggestAnchorAn &&
                        filteredAnalystList.length > 0;

                      return (
                        <div className="position-relative">
                          {groupId ? (
                            <>
                              <Form.Control
                                type="text"
                                data-custom-suggest-input={field.id}
                                value={
                                  selectedAnalyst
                                    ? `${selectedAnalyst.name} (${selectedAnalyst.email})`
                                    : analystSearchTerm
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setSearchUsers({ ...searchUsers, [field.id]: v });
                                  bumpSuggestAnchor(field.id, e.currentTarget);
                                  if (!v) {
                                    handleCustomFieldChange(field.id, '');
                                    setCustomFieldSuggestAnchor((p) => {
                                      const n = { ...p };
                                      delete n[field.id];
                                      return n;
                                    });
                                  }
                                }}
                                onFocus={(e) => {
                                  bumpSuggestAnchor(field.id, e.currentTarget);
                                  if (!analystSearchTerm) {
                                    setFilteredUsers({ ...filteredUsers, [field.id]: analystUsers.slice(0, 10) });
                                  }
                                }}
                                required={isRequired && !isBlocked}
                                disabled={isBlocked}
                                readOnly={isBlocked}
                                placeholder="Digite o nome ou email do analista"
                              />
                              {showAnalystSuggest && suggestAnchorAn && (
                                <div
                                  style={{
                                    position: 'fixed',
                                    top: suggestAnchorAn.top,
                                    left: suggestAnchorAn.left,
                                    width: suggestAnchorAn.width,
                                    zIndex: 1080,
                                    backgroundColor: 'white',
                                    border: '1px solid #ced4da',
                                    borderRadius: '0 0 0.375rem 0.375rem',
                                    maxHeight: '220px',
                                    overflowY: 'auto',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  }}
                                >
                                  {filteredAnalystList.map((analyst) => (
                                    <div
                                      key={analyst.id}
                                      onClick={() => {
                                        handleCustomFieldChange(field.id, analyst.id.toString());
                                        setSearchUsers({ ...searchUsers, [field.id]: '' });
                                        setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                                        setCustomFieldSuggestAnchor((p) => {
                                          const n = { ...p };
                                          delete n[field.id];
                                          return n;
                                        });
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #e9ecef',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                      }}
                                    >
                                      {analyst.name} ({analyst.email})
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-muted">
                              <small>Selecione um grupo primeiro</small>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : field.field_type === 'integer' ? (
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      value={fieldValue || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Apenas inteiros não negativos
                        if (value === '' || /^\d+$/.test(value)) {
                          handleCustomFieldChange(field.id, value);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                      placeholder="0"
                    />
                  ) : field.field_type === 'decimal' ? (
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={fieldValue || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || (parseFloat(v) >= 0 && !/^-/.test(v))) {
                          handleCustomFieldChange(field.id, v);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                      placeholder="0.00"
                    />
                  ) : field.field_type === 'currency' ? (
                    (() => {
                      let currencyValue = { currency: 'BRL', value: '' };
                      if (fieldValue) {
                        try {
                          currencyValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
                        } catch {
                          currencyValue = { currency: 'BRL', value: fieldValue || '' };
                        }
                      }
                      return (
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Select
                            value={currencyValue.currency || 'BRL'}
                            onChange={(e) => {
                              const newCurrency = { ...currencyValue, currency: e.target.value };
                              handleCustomFieldChange(field.id, JSON.stringify(newCurrency));
                            }}
                            disabled={isBlocked}
                            className="csp-wave1-width-150"
                          >
                            <option value="BRL">BRL (R$)</option>
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                          </Form.Select>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={formatCurrencyInputDisplay(
                              currencyValue.value || '',
                              currencyValue.currency || 'BRL'
                            )}
                            onChange={(e) => {
                              const normalized = normalizeCurrencyInput(e.target.value);
                              if (normalized === null) return;
                              const newCurrency = { ...currencyValue, value: normalized };
                              handleCustomFieldChange(field.id, JSON.stringify(newCurrency));
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                            placeholder="Ex.: 30000,01"
                          />
                          {currencyValue.value && (
                            <Form.Text className="text-muted text-nowrap">
                              {formatCurrency(currencyValue.value, currencyValue.currency)}
                            </Form.Text>
                          )}
                        </div>
                      );
                    })()
                  ) : field.field_type === 'table' ? (
                    <CustomTableField
                      value={fieldValue || createDefaultTableValue()}
                      onChange={(nextValue) => handleCustomFieldChange(field.id, nextValue)}
                      disabled={isBlocked}
                    />
                  ) : field.field_type === 'file' ? (
                    <div>
                      <Form.Control
                        type="file"
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          const file = target.files?.[0] || null;
                          if (file) {
                            const error = validateAttachmentFile(file);
                            if (error) {
                              showAlert({
                                title: 'Arquivo inválido',
                                message: error,
                                variant: 'warning',
                              });
                              target.value = '';
                              setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                              return;
                            }
                          }
                          setCustomFieldFiles({ ...customFieldFiles, [field.id]: file });
                        }}
                        disabled={isBlocked}
                        accept={ATTACHMENT_ACCEPT}
                      />
                      {customFieldFiles[field.id] ? (
                        <small className="text-muted d-block mt-1">
                          Arquivo selecionado: {(customFieldFiles[field.id] as File).name}
                        </small>
                      ) : (
                        hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                          <small className="text-success d-block mt-1">
                            {savedCustomFieldFileLabel(customFieldSavedFiles[field.id])}
                          </small>
                        )
                      )}
                    </div>
                  ) : field.field_type === 'files' ? (
                    <div>
                      <Form.Control
                        type="file"
                        multiple
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          const files = target.files ? Array.from(target.files) : null;
                          if (files && files.length > 0) {
                            const error = validateAttachmentFiles(files);
                            if (error) {
                              showAlert({
                                title: 'Arquivo inválido',
                                message: error,
                                variant: 'warning',
                              });
                              target.value = '';
                              setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                              return;
                            }
                          }
                          setCustomFieldFiles({ ...customFieldFiles, [field.id]: files });
                        }}
                        disabled={isBlocked}
                        accept={ATTACHMENT_ACCEPT}
                      />
                      {customFieldFiles[field.id] && Array.isArray(customFieldFiles[field.id]) ? (
                        <small className="text-muted d-block mt-1">
                          {(customFieldFiles[field.id] as File[]).length} arquivo(s) selecionado(s)
                        </small>
                      ) : (
                        hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                          <small className="text-success d-block mt-1">
                            {savedCustomFieldFileLabel(customFieldSavedFiles[field.id])}
                          </small>
                        )
                      )}
                    </div>
                  ) : field.field_type === 'check' ? (
                    (() => {
                      const options: string[] = field.options
                        ? (() => {
                            try {
                              const parsed = JSON.parse(field.options);
                              if (Array.isArray(parsed)) return parsed.map((opt) => String(opt).trim()).filter((opt) => opt.length > 0);
                              if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') return [String(parsed).trim()];
                            } catch {
                              return field.options.split(/[,\n]/).map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0);
                            }
                            return [];
                          })()
                        : [];
                      const hasOptions = options.length > 0;

                      if (hasOptions) {
                        let selectedValues: string[] = [];
                        if (fieldValue) {
                          try {
                            if (typeof fieldValue === 'string') {
                              const parsed = JSON.parse(fieldValue);
                              if (Array.isArray(parsed)) {
                                selectedValues = parsed.map((v) => String(v));
                              } else if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') {
                                selectedValues = [String(parsed)];
                              }
                            } else if (Array.isArray(fieldValue)) {
                              selectedValues = fieldValue.map((v) => String(v));
                            } else {
                              selectedValues = [String(fieldValue)];
                            }
                          } catch {
                            selectedValues = String(fieldValue).trim() ? [String(fieldValue)] : [];
                          }
                        }

                        const selectedValue = selectedValues[0] || '';
                        const shouldShowInfoText = field.checkbox_text_trigger_option
                          ? (field.allow_multiple
                              ? selectedValues.includes(field.checkbox_text_trigger_option)
                              : selectedValue === field.checkbox_text_trigger_option)
                          : (field.allow_multiple ? selectedValues.length > 0 : !!selectedValue);

                        return (
                          <div>
                            {field.checkbox_link && (
                              <div className="mb-2">
                                <a
                                  href={field.checkbox_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  {field.checkbox_link_text || 'Ler Diretrizes'}
                                </a>
                              </div>
                            )}
                            <div>
                              {options.map((option: string, idx: number) => (
                                <Form.Check
                                  key={idx}
                                  className="custom-field-check"
                                  type={field.allow_multiple ? 'checkbox' : 'radio'}
                                  name={field.allow_multiple ? undefined : `field-${field.id}`}
                                  label={option}
                                  checked={field.allow_multiple ? selectedValues.includes(option) : selectedValue === option}
                                  onChange={(e) => {
                                    if (field.allow_multiple) {
                                      const newValues: string[] = e.target.checked
                                        ? [...selectedValues, option]
                                        : selectedValues.filter((v: string) => v !== option);
                                      handleCustomFieldChange(field.id, newValues);
                                      return;
                                    }
                                    handleCustomFieldChange(field.id, e.target.checked ? option : '');
                                  }}
                                  disabled={isBlocked}
                                />
                              ))}
                            </div>
                            {field.checkbox_text && shouldShowInfoText && (
                              <div className="alert alert-info mt-2">
                                {field.checkbox_text}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Checkbox único (comportamento original)
                        const isChecked = fieldValue === 'true' || fieldValue === true || fieldValue === 1 || fieldValue === '1';
                        return (
                          <div>
                            {field.checkbox_link && (
                              <div className="mb-2">
                                <a
                                  href={field.checkbox_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  {field.checkbox_link_text || 'Ler Diretrizes'}
                                </a>
                              </div>
                            )}
                            <Form.Check
                              className="custom-field-check"
                              type="checkbox"
                              label={field.checkbox_label || field.label}
                              checked={isChecked}
                              onChange={(e) => {
                                handleCustomFieldChange(field.id, e.target.checked ? 'true' : 'false');
                              }}
                              disabled={isBlocked}
                            />
                            {isChecked && field.checkbox_text && (
                              <div className="alert alert-info mt-2">
                                {field.checkbox_text}
                              </div>
                            )}
                          </div>
                        );
                    })()
                  ) : field.field_type === 'platform_document' ? (
                    (() => {
                      const currentMode = platformDocumentMode[field.id] || 'upload';
                      const selectedDocumentId = platformDocumentSelectedIds[field.id] || null;
                      
                      return (
                        <div>
                          <Form.Group className="mb-3">
                            <Form.Label>Modo de Seleção</Form.Label>
                            <Form.Select
                              value={currentMode}
                              onChange={(e) => {
                                const newMode = e.target.value as 'upload' | 'platform' | 'create';
                                setPlatformDocumentMode({ ...platformDocumentMode, [field.id]: newMode });
                                // Limpar valores ao trocar de modo
                                if (newMode === 'upload') {
                                  handleCustomFieldChange(field.id, '');
                                  setPlatformDocumentSelectedIds({ ...platformDocumentSelectedIds, [field.id]: null });
                                } else if (newMode === 'platform') {
                                  setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                                } else if (newMode === 'create') {
                                  setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                                  setPlatformDocumentSelectedIds({ ...platformDocumentSelectedIds, [field.id]: null });
                                }
                              }}
                              disabled={isBlocked}
                            >
                              <option value="upload">Upload de Arquivo</option>
                              <option value="platform">Selecionar Documento da Plataforma</option>
                              <option value="create">Criar Novo Documento</option>
                            </Form.Select>
                          </Form.Group>

                          {currentMode === 'upload' ? (
                            <div>
                              <Form.Control
                                type="file"
                                onChange={(e) => {
                                  const target = e.target as HTMLInputElement;
                                  const file = target.files?.[0] || null;
                                  if (file) {
                                    const error = validateAttachmentFile(file);
                                    if (error) {
                                      showAlert({
                                        title: 'Arquivo inválido',
                                        message: error,
                                        variant: 'warning',
                                      });
                                      target.value = '';
                                      setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                                      return;
                                    }
                                  }
                                  setCustomFieldFiles({ ...customFieldFiles, [field.id]: file });
                                  // Limpar erro quando arquivo é selecionado
                                  if (file) {
                                    setCustomFieldInlineErrors({ ...customFieldInlineErrors, [field.id]: '' });
                                  }
                                }}
                                disabled={isBlocked}
                                accept={ATTACHMENT_ACCEPT}
                                isInvalid={
                                  !!customFieldInlineErrors[field.id] &&
                                  !customFieldFiles[field.id] &&
                                  !hasSavedCustomFieldFile(customFieldSavedFiles[field.id])
                                }
                              />
                              {customFieldFiles[field.id] ? (
                                <small className="text-success d-block mt-1">
                                  Arquivo selecionado: {(customFieldFiles[field.id] as File).name}
                                </small>
                              ) : (
                                hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                                  <small className="text-success d-block mt-1">
                                    {savedCustomFieldFileLabel(customFieldSavedFiles[field.id])}
                                  </small>
                                )
                              )}
                              {customFieldInlineErrors[field.id] &&
                                !customFieldFiles[field.id] &&
                                !hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {customFieldInlineErrors[field.id]}
                                </Form.Control.Feedback>
                              )}
                            </div>
                          ) : currentMode === 'platform' ? (
                            <div>
                              <Form.Select
                                value={selectedDocumentId || ''}
                                onChange={(e) => {
                                  const docId = e.target.value ? parseInt(e.target.value) : null;
                                  setPlatformDocumentSelectedIds({ ...platformDocumentSelectedIds, [field.id]: docId });
                                  // Armazenar o ID do documento no campo customizado para uso no submit
                                  handleCustomFieldChange(field.id, docId ? docId.toString() : '');
                                  // Limpar erro quando documento é selecionado
                                  if (docId) {
                                    setCustomFieldInlineErrors({ ...customFieldInlineErrors, [field.id]: '' });
                                  }
                                }}
                                disabled={isBlocked}
                                required={isRequired && !isBlocked}
                                isInvalid={!!customFieldInlineErrors[field.id] && !selectedDocumentId}
                              >
                                <option value="">Selecione um documento...</option>
                                {(field.document_type_id && documentInstancesByField[field.id] 
                                  ? documentInstancesByField[field.id] 
                                  : documentInstances).map((doc) => (
                                  <option key={doc.id} value={doc.id}>
                                    {doc.document_type?.name || 'Documento'} {doc.title ? `- ${doc.title}` : ''} ({new Date(doc.created_at).toLocaleDateString('pt-BR')})
                                  </option>
                                ))}
                              </Form.Select>
                              {customFieldInlineErrors[field.id] && !selectedDocumentId && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {customFieldInlineErrors[field.id]}
                                </Form.Control.Feedback>
                              )}
                              {selectedDocumentId && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="mt-2 p-0"
                                  onClick={() => window.open(`/documents/${selectedDocumentId}/edit`, '_blank')}
                                  disabled={isBlocked}
                                >
                                  Abrir documento em nova aba
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div>
                              <Button
                                variant="outline-primary"
                                onClick={() => {
                                  window.open('/documents', '_blank');
                                }}
                                disabled={isBlocked}
                              >
                                Abrir Tela de Documentos
                              </Button>
                              <Form.Text className="text-muted d-block mt-2">
                                Uma nova aba será aberta para você criar um novo documento. Após criar e finalizar o documento, volte aqui e selecione-o na opção "Selecionar Documento da Plataforma".
                              </Form.Text>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : null}
                </Form.Group>
              );
            };

            // Se não há grupos e há apenas campos sem agrupamento, exibir sem tabs
            if (!hasGroups && hasUngrouped) {
              return (
                <div className="mb-3">
                  <h6>Informações do Processo</h6>
                  {ungroupedFields.map(renderField)}
                </div>
              );
            }

            // Se há grupos, sempre exibir com tabs (mesmo que seja apenas um grupo)
            // Isso garante consistência visual e permite adicionar mais grupos no futuro
            const firstGroupKey = tabKeys.length > 0 ? tabKeys[0] : (Object.keys(groupedFields)[0] || 'ungrouped');
            const currentActiveTab = activeTab || firstGroupKey;
            
            // Obter campos da aba ativa
            const getActiveTabFields = () => {
              if (currentActiveTab === 'ungrouped') {
                return ungroupedFields;
              }
              return groupedFields[currentActiveTab] || [];
            };
            
            const activeFields = getActiveTabFields();
            
            return (
              <div className="mb-3" ref={customFieldsRef}>
                <h6>Informações do Processo</h6>
                {/* Renderizar campos da aba ativa */}
                <div className="mb-2">
                  {activeFields.map(renderField)}
                </div>
                {/* Mostrar nomes das abas abaixo dos campos */}
                <div className="d-flex gap-2 flex-wrap mt-3 pt-2 border-top">
                  {tabKeys.map((tabKey) => {
                    const tabName = tabKey === 'ungrouped' ? 'Geral' : tabKey;
                    const isActive = currentActiveTab === tabKey;
                    return (
                      <span
                        key={tabKey}
                        className={`badge ${isActive ? 'bg-primary' : 'bg-secondary'}`}
                        style={{ 
                          fontSize: '0.9rem', 
                          padding: '6px 12px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setActiveTab(tabKey);
                          // Focar no início dos campos após um pequeno delay
                          setTimeout(() => {
                            if (customFieldsRef.current) {
                              const firstField = customFieldsRef.current.querySelector('input, select, textarea') as HTMLElement;
                              if (firstField) {
                                firstField.focus();
                              }
                            }
                          }, 100);
                        }}
                      >
                        {tabName}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })() : null}

          {/* Campos reposicionados: Grupo para Encaminhar e Aprovador da Requisição */}
          <Form.Group className="mb-3">
            <Form.Label>Grupo para Encaminhar <span className="text-danger">*</span></Form.Label>
            <Form.Select 
              value={groupId} 
              onChange={(e) => setGroupId(e.target.value)}
              required={!(!workflowId || availableGroups.length === 0)}
              disabled={!workflowId || availableGroups.length === 0}
            >
              <option value="">
                {!workflowId 
                  ? 'Selecione um tipo primeiro' 
                  : availableGroups.length === 0 
                    ? 'Nenhum grupo disponível para este tipo'
                    : 'Selecione um grupo'}
              </option>
              {availableGroups.map((group: Group) => (
                <option key={group.id} value={String(group.id)}>
                  {group.name || `Grupo #${group.id}`}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {workflowId 
                ? 'Selecione um grupo que atende ao Primeiro Contato deste tipo de contratação.'
                : 'Toda requisição de compra deve ser encaminhada para um grupo associado ao tipo de contratação.'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {/* Botões de navegação entre abas */}
          {customFields.length > 0 && tabKeys.length > 1 && (() => {
            const currentIndex = tabKeys.indexOf(activeTab);
            const canGoPrevious = currentIndex > 0;
            const canGoNext = currentIndex < tabKeys.length - 1;
            
            const navigateTab = (direction: 'prev' | 'next') => {
              if (direction === 'prev' && canGoPrevious) {
                const newTab = tabKeys[currentIndex - 1];
                setActiveTab(newTab);
                setTimeout(() => {
                  if (customFieldsRef.current) {
                    const firstField = customFieldsRef.current.querySelector('input, select, textarea') as HTMLElement;
                    if (firstField) {
                      firstField.focus();
                    }
                  }
                }, 100);
              } else if (direction === 'next' && canGoNext) {
                const newTab = tabKeys[currentIndex + 1];
                setActiveTab(newTab);
                setTimeout(() => {
                  if (customFieldsRef.current) {
                    const firstField = customFieldsRef.current.querySelector('input, select, textarea') as HTMLElement;
                    if (firstField) {
                      firstField.focus();
                    }
                  }
                }, 100);
              }
            };
            
            return (
              <div className="d-flex align-items-center me-auto">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigateTab('prev')}
                  disabled={!canGoPrevious}
                  style={{ 
                    minWidth: '36px',
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    lineHeight: '1',
                    fontWeight: 'bold'
                  }}
                  title="Aba anterior"
                >
                  ◀
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigateTab('next')}
                  disabled={!canGoNext}
                  style={{ 
                    minWidth: '36px',
                    padding: '6px 10px',
                    marginLeft: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    lineHeight: '1',
                    fontWeight: 'bold'
                  }}
                  title="Próxima aba"
                >
                  ▶
                </Button>
              </div>
            );
          })()}
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button 
            variant="outline-primary" 
            onClick={async () => {
              // Salvar como rascunho sem validar campos obrigatórios
              try {
                setLoading(true);
                
                const saved = await savePurchaseDraftRecord(
                  API_URL,
                  draftId,
                  buildPurchaseDraftPayload({
                    object,
                    itemsDescription,
                    urgency,
                    priority,
                    includePriority: canManagePriority,
                    workflowId,
                    groupId,
                    requesterId: currentUser?.id ?? null,
                  }),
                  { recreateIfNotDraft: true }
                );
                if (!saved.id) {
                  throw new Error('Não foi possível salvar o rascunho.');
                }
                const savedDraftId = saved.id;
                setDraftId(savedDraftId);
                setIsDraft(true);
                
                // Salvar campos personalizados
                if (savedDraftId && customFields.length > 0) {
                  const customFieldPromises = customFields
                    .filter(field => {
                      const fieldValue = customFieldValues[field.id];
                      const mode = platformDocumentMode[field.id] || 'upload';
                      if (field.field_type === 'platform_document') {
                        return mode === 'platform' && fieldValue !== undefined && fieldValue !== '' && fieldValue !== null;
                      }
                      if (field.field_type === 'check' && !field.allow_multiple) {
                        return hasCheckFieldPersistedValue(field, fieldValue);
                      }
                      return fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && 
                             !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                             !(field.field_type === 'table' && !hasTableContent(fieldValue)) &&
                             field.field_type !== 'file' && field.field_type !== 'files';
                    })
                    .map(async (field) => {
                      const fieldValue = customFieldValues[field.id];
                      const valueData: any = {
                        purchase_request_id: savedDraftId,
                        custom_field_id: field.id,
                      };
                      
                      if (field.field_type === 'platform_document' && platformDocumentMode[field.id] === 'platform') {
                        const docId = platformDocumentSelectedIds[field.id];
                        if (docId) {
                          valueData.document_instance_id = docId;
                          valueData.text_value = null;
                          valueData.file_path = null;
                          valueData.file_paths = null;
                        }
                      } else if (field.field_type === 'table') {
                        valueData.text_value = serializeTableValue(fieldValue);
                      } else if (isSupplierCustomField(field)) {
                        valueData.text_value = normalizeSupplierFieldRawValue(fieldValue);
                        if (!valueData.text_value) {
                          return null;
                        }
                      } else if (field.field_type === 'currency' || field.field_type === 'period') {
                        valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
                      } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
                        valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
                          ? JSON.stringify(fieldValue)
                          : String(fieldValue);
                      } else if (field.field_type === 'check' && !field.allow_multiple) {
                        const checkSerialized = serializeCheckFieldValue(field, fieldValue);
                        if (!checkSerialized) {
                          return;
                        }
                        valueData.text_value = checkSerialized;
                      } else {
                        const stringValue = String(fieldValue);
                        if (stringValue === '[object Object]') {
                          return null;
                        }
                        valueData.text_value = stringValue;
                      }
                      
                      try {
                        await upsertPurchaseCustomFieldValue(API_URL, valueData);
                      } catch (error: unknown) {
                        console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
                      }
                    });
                  
                  await Promise.all(customFieldPromises);
                }

                // Persistir anexos do rascunho (file/files/platform_document em modo upload)
                if (savedDraftId) {
                  const draftUploadResult = await uploadPurchaseCustomFieldFiles({
                    apiUrl: API_URL,
                    purchaseRequestId: savedDraftId,
                    customFields,
                    customFieldFiles,
                    platformDocumentMode,
                  });

                  if (Object.keys(draftUploadResult.savedByFieldId).length > 0) {
                    setCustomFieldSavedFiles((prev) => ({
                      ...prev,
                      ...draftUploadResult.savedByFieldId,
                    }));
                    setCustomFieldFiles((prev) => {
                      const next = { ...prev };
                      draftUploadResult.uploadedFieldIds.forEach((fieldId) => {
                        next[fieldId] = null;
                      });
                      return next;
                    });
                  }

                  if (draftUploadResult.errors.length > 0) {
                    showAlert({
                      title: 'Rascunho salvo com avisos',
                      message:
                        'Os dados do rascunho foram salvos, mas alguns anexos falharam no upload.',
                      variant: 'warning',
                      details: draftUploadResult.errors.join('\n'),
                    });
                  } else {
                    showAlert({
                      title: 'Sucesso',
                      message: 'Rascunho salvo com sucesso!',
                      variant: 'success',
                    });
                  }
                } else {
                  showAlert({
                    title: 'Sucesso',
                    message: 'Rascunho salvo com sucesso!',
                    variant: 'success',
                  });
                }
                } catch (error: any) {
                  console.error('Erro ao salvar rascunho:', error);
                  let errorDetails = 'Erro desconhecido';
                  if (error.response?.data?.detail) {
                    const detail = error.response.data.detail;
                    if (Array.isArray(detail)) {
                      errorDetails = formatFastApiValidationErrors(detail);
                    } else if (typeof detail === 'object') {
                      errorDetails = JSON.stringify(detail);
                    } else {
                      errorDetails = String(detail);
                    }
                  }
                  showAlert({
                    title: 'Erro ao Salvar Rascunho',
                    message: 'Não foi possível salvar o rascunho.',
                    variant: 'danger',
                    details: errorDetails
                  });
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            <i className="bi bi-save me-2"></i>
            Salvar Como Rascunho
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? 'Enviando...' : 'Enviar Requisição'}
          </Button>
        </Modal.Footer>
      </Form>
      </Modal>
    </>
  );
};

export default Purchases;


